import arMessages from "./locales/ar.json";
import deMessages from "./locales/de.json";
import esMessages from "./locales/es.json";
import frMessages from "./locales/fr.json";
import jaMessages from "./locales/ja.json";
import koMessages from "./locales/ko.json";
import ptBRMessages from "./locales/pt-BR.json";
import ruMessages from "./locales/ru.json";
import zhTWMessages from "./locales/zh-TW.json";
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  normalizeLocale,
  type Locale
} from "./locale";

export {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  SUPPORTED_LOCALES,
  getLocaleDirection,
  getLocaleLabel,
  normalizeLocale,
  type Locale
} from "./locale";

export type MessageParams = Record<string, string | number | boolean | null | undefined>;
export type Translate = (key: MessageKey, params?: MessageParams) => string;

const zhCN_MESSAGES = {
  "publish.handleReclaimed": "本次领回也算更换地址；当前地址将开始新的 30 天跳转和 180 天保护期。",
  "handleEdit.reclaimAvailable": "这是你可以领回的旧地址。",
  "handleEdit.usePrevious": "使用此地址",
  "handleEdit.deadlines": "跳转截至 {redirectDate}；保护截至 {releaseDate}。",
  "handleEdit.previous": "受保护的旧地址",
  "global.siteDescription": "用 30 秒认真介绍自己，让别人快速认识你。",
  "global.languageSwitch": "切换语言",
  "global.languageCurrent": "当前语言：{locale}",
  "global.language.zh-CN": "中文",
  "global.language.zh-TW": "繁體中文",
  "global.language.en": "English",
  "global.language.ja": "日本語",
  "global.language.ko": "한국어",
  "global.language.es": "Español",
  "global.language.fr": "Français",
  "global.language.de": "Deutsch",
  "global.language.pt-BR": "Português (Brasil)",
  "global.language.ru": "Русский",
  "global.language.ar": "العربية",
  "common.close": "关闭",
  "common.cancel": "取消",
  "common.save": "保存",
  "common.delete": "删除",
  "common.edit": "编辑",
  "common.manage": "管理",
  "common.login": "登录",
  "common.logout": "退出",
  "common.loggingOut": "退出中",
  "common.logoutRetry": "重试退出",
  "common.preview": "预览",
  "common.email": "邮箱",
  "common.inviteCode": "邀请码",
  "common.loading": "加载中",
  "editor.publish.processing": "仍在处理中",
  "common.localDraft": "本机草稿",
  "common.publicBeta": "公开测试中",
  "common.serviceStatus": "运行状态",
  "common.optional": "可选",
  "common.page": "页面",
  "common.reason": "原因",
  "common.description": "补充说明",
  "common.submit": "提交",
  "common.listSeparator": "、",
  "nav.login": "登录",
  "nav.draftFirst": "先写草稿",
  "home.action.create": "创建我的介绍",
  "home.action.continue": "继续写",
  "home.action.manage": "管理介绍",
  "home.action.agent": "Agent 代笔",
  "home.meta.title": "30s.to — 30 秒，让别人认识你。",
  "home.meta.description": "一人前行，更需要让别人了解自己。30 秒，让别人认识你。你是谁、在做什么、做过什么、能提供什么资源。让别人找到感兴趣的点，建立连接。",
  "home.title": "30 秒，\n让别人认识你。",
  "home.kicker": "一人前行，更需要让别人了解自己",
  "home.value.copy": "你是谁、在做什么、做过什么、能提供什么资源。让别人找到感兴趣的点，建立连接。",
  "home.freeNote": "创建、发布、分享，永久免费。",
  "home.features.label": "产品特点",
  "home.features.time.tab": "30 秒读完",
  "home.why.copy": "只写最重要的。介绍越长，越少人读完。30 秒，刚刚好。",
  "home.principle.expression.title": "自由表达",
  "home.principle.expression.copy": "经历、作品、想法与资源，按自己的方式组织。写下你想让别人了解的自己。",
  "home.principle.link.title": "专属 30s.to 链接",
  "home.principle.link.copy": "30s.to/@you，简短、好记，也直白：30 秒，认识你。",
  "home.principle.theme.title": "丰富主题表达自己",
  "home.principle.theme.copy": "清爽、复古，或大胆鲜明。选一个像你的主题，让别人从第一眼就感受到你的风格。",
  "home.footer.tagline": "少写一点，重要的才会被看见。",
  "home.footer.navigation": "页脚导航",
  "home.demo.linkNotice": "这是一个示例链接。创建你的 30 秒介绍后，就可以换成自己的。",
  "home.demo.profile.name": "林夏 / Lin Xia",
  "home.demo.profile.identity": "把复杂问题做成简单产品的独立设计师",
  "home.demo.profile.tag.product": "产品设计",
  "home.demo.profile.tag.indie": "独立产品",
  "home.demo.profile.tag.ai": "AI 工具",
  "home.demo.profile.body": "## 最近\n我正在做访谈整理工具 Briefly，想帮独立设计师少花时间搬运笔记，多一点时间理解人。做产品前，我习惯先陪用户走一遍真实流程。\n\n## 我的经历\n2020—2023 年在协作软件团队做产品设计，负责编辑器和新用户引导，和工程师一起把复杂操作拆成可完成的小步骤。\n\n2024 年起独立工作，为早期团队做用户研究和交互设计，也持续打磨自己的小产品。\n\n## 我的项目\n- [Relay](https://example.com/projects/relay)：我负责设计的团队决策记录工具，把结论、理由和负责人放在一起，方便后来加入的人理解背景。\n- [Briefly](https://example.com/projects/briefly)：正在小范围试用的访谈笔记工具，把零散记录整理成可回溯的主题，保留原话，方便设计师核对判断。\n\n## 可以找我\n如果你有用户反馈，却不知道先改什么，可以找我一起梳理问题、做可测试的原型。我也愿意分享访谈提纲和研究记录模板，适合刚开始接触用户研究的小团队。\n\n## 一起聊聊\n想认识认真做小产品的人，聊聊第一次用户访谈、产品取舍和独立工作。周末常去逛街角小店——有时，一张手写价目表比产品说明更值得琢磨。",
  "home.demo.profile.link.website": "作品",
  "home.demo.profile.link.github": "GitHub",
  "home.demo.profile.link.calendar": "约个时间",
  "handleStart.kicker": "公开地址",
  "handleStart.title": "选择你的 30s.to 地址",
  "handleStart.copy": "先选一个容易记住的地址，再写 30 秒介绍。已有草稿内容会继续保留。",
  "handleStart.label": "你的地址",
  "handleStart.help.empty": "4-24 位小写英文字母。现在只是保存草稿，发布时确认可用。",
  "handleStart.help.ok": "格式可用，发布时会再次确认可用。",
  "handleStart.submit": "继续写介绍",
  "handleAvailability.checking": "正在检查这个地址…",
  "handleAvailability.available": "当前可用，发布成功后正式归你使用。",
  "handleAvailability.owned": "这是你当前拥有的地址。",
  "handleAvailability.assigned": "这是分配给你的地址，发布成功后生效。",
  "handleAvailability.failed": "暂时无法确认地址状态，请重新检查。",
  "handleAvailability.rateLimited": "检查过于频繁，请在 {minutes} 分钟后再试。",
  "handleAvailability.retry": "重新检查",
  "handleAvailability.suggestions": "也可以试试",
  "handleAvailability.cooldown": "公开地址仍在 30 天更换冷却期内，可在 {date} 后再次更换；你可以恢复为当前已发布地址。",
  "editor.shell.loading": "加载创建流程",
  "editor.shell.workspace": "30 秒介绍工作区",
  "editor.shell.canvas": "30 秒介绍写作台",
  "editor.command.aria": "编辑命令栏",
  "editor.command.publish": "发布",
  "editor.command.publishUpdate": "发布修改",
  "editor.command.publishSettings": "发布设置",
  "editor.command.dashboard": "管理页",
  "editor.publish.success": "已发布",
  "editor.publish.successAfterLogin": "已登录并发布",
  "editor.publish.publishing": "正在发布…",
  "editor.publish.publishingDetail": "正在生成你的公开页，请稍候。",
  "editor.publish.successDetail": "公开页已更新，可以打开查看。",
  "editor.publish.viewPublic": "查看公开页",
  "editor.publish.cacheFailed": "但公开页缓存写入失败。",
  "editor.command.login": "登录",
  "editor.handle.editTitle": "修改公开地址",
  "editor.handle.claimAfterPublish": "发布后归你使用",
  "editor.handle.claimAfterPublishTitle": "发布后该地址归你使用",
  "editor.status.signedIn": "已登录",
  "editor.status.account": "账号：{email}",
  "editor.status.localTitle": "本机草稿",
  "editor.status.localCopy": "当前草稿只保存在这台设备。登录后会同步到云端。",
  "editor.status.location": "保存位置",
  "editor.status.state": "保存状态",
  "editor.status.emailLogin": "邮箱登录",
  "editor.autosave.savingShort": "保存中",
  "editor.autosave.savedCloudShort": "云端已保存",
  "editor.autosave.savedLocalShort": "已保存到本机",
  "editor.autosave.failedShort": "保存失败",
  "editor.autosave.signedInShort": "已登录",
  "editor.autosave.cloud": "云端",
  "editor.autosave.retry": "需要重试",
  "editor.autosave.device": "当前设备",
  "editor.autosave.saving": "正在自动保存...",
  "editor.autosave.savedLocal": "已自动保存到本地",
  "editor.autosave.savedCloud": "已自动保存到云端",
  "editor.autosave.authExpired": "登录已失效，已保存到本地",
  "editor.autosave.cloudFailed": "云端保存失败，已保存到本地",
  "editor.autosave.cloudUnavailable": "云端保存暂未接入，已保存到本地",
  "editor.autosave.failed": "自动保存失败，请复制内容后重试",
  "editor.autosave.enabled": "自动保存已开启",
  "identity.namePlaceholder": "你的名字",
  "identity.nameAria": "姓名或昵称",
  "identity.identityPlaceholder": "一句话身份",
  "identity.identityAria": "一句话身份",
  "identity.tagsAria": "标签",
  "identity.addTag": "添加标签",
  "identity.removeTag": "删除标签 {tag}",
  "body.emptyInstruction": "写下别人第一次认识你时最值得知道的几件事。\n少写一点，重要的才会被看见。30s，刚刚好。",
  "body.templatesLabel": "也可以从模版开始",
  "body.templatesAria": "正文身份模板",
  "body.templateExampleLabel": "模拟样例",
  "body.viewTemplateExample": "查看样例",
  "links.sectionAria": "链接",
  "links.documentTitle": "链接",
  "links.documentCopy": "社交主页、预约和联系方式",
  "links.documentEmptyCopy": "添加社交主页、预约或联系方式，之后可以随时修改。",
  "links.add": "添加链接",
  "links.addType": "添加 {label}",
  "links.edit": "编辑 {label}",
  "links.addedAria": "已添加链接",
  "links.closePanel": "关闭链接编辑",
  "links.editTitle": "编辑链接",
  "links.addTitle": "添加链接",
  "links.chooseTypeAgain": "重新选择链接类型",
  "links.calendarProvider": "预约平台",
  "links.displayName": "显示名称",
  "links.duplicate": "{label} 已经添加过。",
  "links.alreadyAdded": "{label} 已经添加过",
  "links.bookingAlreadyAdded": "预约已经添加过",
  "links.remove": "删除",
  "links.category.social": "社交",
  "links.category.booking": "预约",
  "links.category.work": "作品 / 主页",
  "links.category.content": "内容",
  "links.category.contact": "联系",
  "links.category.other": "其他",
  "links.type.x": "X",
  "links.type.github": "GitHub",
  "links.type.linkedin": "LinkedIn",
  "links.type.instagram": "Instagram",
  "links.type.youtube": "YouTube",
  "links.type.bilibili": "Bilibili",
  "links.type.xiaohongshu": "小红书",
  "links.type.wechat": "微信公众号",
  "links.type.email": "Email",
  "links.type.calendar": "预约",
  "links.type.website": "网站",
  "links.type.newsletter": "Newsletter",
  "links.type.tiktok": "TikTok",
  "links.type.threads": "Threads",
  "links.type.mastodon": "Mastodon",
  "links.type.dribbble": "Dribbble",
  "links.type.jike": "即刻",
  "links.type.other": "Other",
  "links.input.x": "X handle",
  "links.input.github": "GitHub handle",
  "links.input.linkedin": "LinkedIn URL",
  "links.input.instagram": "Instagram handle",
  "links.input.youtube": "YouTube handle",
  "links.input.bilibili": "Bilibili UID",
  "links.input.xiaohongshu": "小红书主页 URL",
  "links.input.wechat": "公众号 ID",
  "links.input.email": "邮箱",
  "links.input.calendar": "预约路径",
  "links.input.website": "网站 URL",
  "links.input.newsletter": "订阅 URL",
  "links.input.tiktok": "TikTok handle",
  "links.input.threads": "Threads handle",
  "links.input.mastodon": "Mastodon URL",
  "links.input.dribbble": "Dribbble handle",
  "links.input.jike": "即刻主页 URL",
  "links.input.other": "链接 URL",
  "links.input.calendar.cal": "Cal.com 路径",
  "links.input.calendar.calendly": "Calendly 路径",
  "links.input.calendar.google": "Google Calendar 预约页",
  "links.input.calendar.microsoft": "Microsoft Bookings 链接",
  "links.input.calendar.hubspot": "HubSpot Meetings 链接",
  "links.input.calendar.savvycal": "SavvyCal 路径",
  "links.input.calendar.tidycal": "TidyCal 路径",
  "links.input.calendar.zcal": "zcal 路径",
  "links.hint.handle": "只填 handle，不需要 URL。",
  "links.hint.uid": "只填 UID，不需要主页链接。",
  "links.hint.slug": "只填路径，不需要完整 URL。",
  "links.hint.email": "会生成 mailto: 联系方式。",
  "links.hint.text": "公开页点击后复制该内容。",
  "links.hint.url": "请输入平台允许的 https:// 链接。",
  "links.hint.calendarSlug": "只填用户名或预约路径，不需要完整 URL。",
  "links.hint.calendarUrl": "请输入该平台的完整预约页链接。",
  "rich.toolbarAria": "正文格式工具栏",
  "rich.heading": "章节标题",
  "rich.paragraph": "普通段落",
  "rich.bold": "加粗",
  "rich.boldFallback": "加粗文字",
  "rich.italic": "斜体",
  "rich.italicFallback": "斜体文字",
  "rich.code": "行内代码",
  "rich.comment": "切换注释",
  "rich.ul": "无序列表",
  "rich.ol": "有序列表",
  "rich.link": "链接",
  "rich.linkPrompt": "输入 https:// 或 mailto: 链接",
  "rich.linkInvalid": "链接只支持 https:// 或 mailto:",
  "rich.linkFallback": "链接文字",
  "rich.aria": "30 秒介绍正文",
  "rich.more": "更多格式",
  "rich.quote": "引用",
  "rich.strikethrough": "删除线",
  "rich.strikethroughFallback": "删除线文字",
  "rich.codeBlock": "代码块",
  "rich.codeBlockFallback": "代码",
  "rich.taskList": "任务列表",
  "rich.table": "表格",
  "rich.tableSkeleton": "| 项目 | 说明 |\n| --- | --- |\n| 项目一 | 说明一 |\n| 项目二 | 说明二 |",
  "rich.indent": "增加缩进",
  "rich.outdent": "减少缩进",
  "rich.placeholder": "用 Markdown 写下 30 秒内能读完的介绍...",
  "feedback.aria": "写作反馈",
  "feedback.timeTitle": "预计阅读时间",
  "feedback.preview": "预览",
  "feedback.previewMenu": "打开预览菜单",
  "feedback.openAcceptancePreview": "打开验收预览",
  "feedback.acceptancePreviewCopy": "在当前设备查看公开页效果",
  "feedback.noIssues": "没有阻断问题",
  "feedback.prePublish": "发布前检查",
  "feedback.ready": "无阻断项",
  "feedback.itemCount": "{count} 项",
  "feedback.valid": "内容满足发布校验。",
  "issue.action.displayName": "填写姓名",
  "issue.action.oneLineIdentity": "填写身份",
  "issue.action.tags": "添加标签",
  "issue.action.body": "回到正文",
  "issue.action.handle": "选择地址",
  "issue.action.links": "管理链接",
  "issue.action.view": "查看",
  "issue.short.displayName": "缺姓名",
  "issue.short.oneLineIdentity": "缺身份",
  "issue.short.tags": "缺标签",
  "issue.short.handle": "缺地址",
  "issue.short.readingTime": "超过 60s",
  "issue.short.readingTimeLong": "超过 30s",
  "issue.short.readingTimeShort": "内容偏少",
  "issue.short.bodyPlaceholder": "正文占位",
  "issue.short.bodyFormat": "正文格式",
  "issue.short.links": "链接有问题",
  "issue.short.linksEmpty": "可添加链接",
  "issue.short.pending": "待完善",
  "issue.summary.more": "{count} 项待完善",
  "publish.title.create": "确认发布",
  "publish.title.update": "确认发布修改",
  "publish.action.create": "发布到 30s.to",
  "publish.action.update": "发布修改",
  "publish.closeAria": "关闭发布确认",
  "publish.address": "公开地址",
  "publish.unpublishedChanges": "有未发布修改",
  "publish.handleChanged": "公开地址将从 @{oldHandle} 改为 @{newHandle}。旧地址跳转 30 天；改名满 180 天后可能分配给其他人，旧链接可能指向其介绍。",
  "publish.readingTime": "阅读时间",
  "publish.status": "状态",
  "publish.ready": "可发布",
  "publish.todo": "{count} 项待完善",
  "publish.visibility": "公开方式",
  "publish.visibility.indexed": "允许搜索引擎收录",
  "publish.visibility.unlisted": "仅通过链接",
  "publish.visibility.indexedNote": "任何人都能查看，并可能出现在搜索结果中。",
  "publish.visibility.unlistedNote": "任何拿到链接的人都能查看；我们不会主动提交给搜索引擎。",
  "publish.visibility.publicNote": "两种方式都是公开访问，链接也可以被转发。想只自己查看，请继续保留为草稿。",
  "publish.publicContent": "发布后，姓名、身份、标签、正文和链接将公开。作者注释不会展示，但不应存放秘密信息。",
  "publish.noop": "公开页已是最新。修改内容或公开性后再发布。",
  "publish.valid": "内容满足发布校验。",
  "handleEdit.title": "修改公开地址",
  "handleEdit.label": "30s.to 地址",
  "handleEdit.help.changed": "保存仅修改草稿，发布后生效。旧地址跳转 30 天，改名满 180 天后可能分配给其他人。",
  "handleEdit.help.saved": "保存到草稿，发布时会再次确认可用。",
  "handleEdit.note": "当前公开地址仍是 30s.to/@{handle}，新地址发布成功后才会生效。",
  "handleEdit.save": "保存到草稿",
  "conflict.title": "选择草稿版本",
  "conflict.copy": "这台设备和云端都有未发布内容。选择后会作为接下来编辑的版本。",
  "conflict.useLocal": "使用本机草稿",
  "conflict.useLocalCopy": "保留当前页面内容，并同步到云端。",
  "conflict.useCloud": "使用云端草稿",
  "conflict.untitled": "未命名",
  "conflict.cloudVersion": "云端保存的版本",
  "conflict.modifiedAt": "最后修改 · {time}",
  "conflict.time.justNow": "刚刚",
  "conflict.time.minutesAgo": "{count} 分钟前",
  "conflict.time.hoursAgo": "{count} 小时前",
  "conflict.time.daysAgo": "{count} 天前",
  "authModal.title": "登录 30s.to",
  "authModal.copy": "登录后草稿会同步到云端，发布和管理也会更稳。",
  "login.page.eyebrow": "Account",
  "login.page.title": "登录 30s.to",
  "login.page.copy": "同步你的草稿、发布公开页，并管理邀请码。",
  "login.invalidEmail": "请输入有效邮箱。",
  "login.sendFailed": "验证码发送失败。",
  "login.devCodeReady": "本地验证码已生成。",
  "login.codeSent": "验证码已发送，请检查邮箱。",
  "login.networkSendFailed": "验证码发送失败，请检查网络。",
  "login.invalidCode": "请输入 6 位验证码。",
  "login.codeInvalid": "验证码不正确或已过期。",
  "login.failed": "登录失败，请稍后重试。",
  "login.sendBusy": "发送中...",
  "login.resendCode": "重新发送验证码",
  "login.sendCode": "发送验证码",
  "login.localCode": "本地开发验证码：{code}",
  "login.code": "验证码",
  "login.codePlaceholder": "6 位数字",
  "login.inviteHelp": "Public Beta 阶段需要邀请码。验证成功后会激活账号。",
  "login.verifyBusy": "验证中...",
  "login.verifyInvite": "验证邀请码并登录",
  "login.verify": "登录并继续",
  "login.footnote": "无密码登录。新用户在邮箱验证后使用邀请码激活。",
  "login.google": "使用 Google 继续",
  "login.github": "使用 GitHub 继续",
  "login.emailDivider": "或使用邮箱",
  "login.oauthFailed": "社交登录未完成，请重新开始或改用邮箱登录。",
  "login.linkTitle": "验证账号邮箱",
  "login.linkPageCopy": "这个邮箱已有 30s.to 账号。验证后才会建立关联，不会自动合并。",
  "login.linkCopy": "验证码将发送到 {email}。邮箱不可在此修改。",
  "login.linkVerify": "验证并关联",
  "login.linkRequired": "检测到已有账号，请先验证邮箱。",
  "login.activateTitle": "激活 30s.to",
  "login.activatePageCopy": "社交身份已验证。邀请码成功前不会创建永久账号。",
  "login.activateCopy": "Public Beta 阶段需要邀请码。邀请码只会在账号创建成功时兑换。",
  "login.activationOpenCopy": "当前已开放注册。继续即可激活账号。",
  "login.activationClosedCopy": "当前暂不开放新账号激活，已有账号仍可正常登录。",
  "login.activate": "激活并继续",
  "login.inviteRequired": "邮箱已验证，请使用邀请码激活。",
  "login.useEmailInstead": "改用邮箱登录",
  "preview.emptyEyebrow": "Preview",
  "preview.emptyTitle": "没有可预览的介绍。",
  "preview.backToEdit": "回到编辑",
  "preview.edit": "编辑",
  "preview.localNoteAria": "本机预览说明",
  "preview.localNote": "本机验收",
  "preview.localTitle": "当前设备草稿预览",
  "preview.themePicker": "主题切换",
  "preview.themeGroup": "选择主题",
  "preview.themeFree": "基础",
  "preview.themePro": "Pro",
  "preview.mobileAria": "移动端公开页预览",
  "preview.close": "关闭预览",
  "preview.editorTitle": "预览",
  "preview.publicTitle": "公开页预览",
  "preview.shareCardButton": "查看主题名片",
  "preview.shareCardDialogTitle": "主题名片预览",
  "preview.shareCardTitle": "{theme} 主题名片",
  "preview.shareCardLogin": "登录后生成真实预览",
  "preview.shareCardLoading": "正在生成主题名片…",
  "preview.shareCardError": "暂时无法生成名片预览",
  "preview.shareCardNote": "主题专属款会使用与公开页一致的构图语言；通用款可在管理页选择。",
  "public.copySuccess": "已复制 {label}",
  "public.copyFailed": "复制失败，请手动复制。",
  "public.createMine": "创建我的介绍",
  "public.report": "举报这个页面",
  "public.ownerAria": "个人管理入口",
  "public.owner.manage": "管理",
  "public.owner.edit": "编辑",
  "public.status.unavailable": "不可访问",
  "public.status.notFound": "404",
  "public.status.moderated": "这个 30 秒介绍暂不可访问。",
  "public.status.frozen": "这个公开地址暂不可访问。",
  "public.status.unpublished": "这个 30 秒介绍还没有发布。",
  "theme.footer": "Made with 30s.to",
  "theme.fallback.name": "Your Name",
  "theme.fallback.nameUpper": "YOUR NAME",
  "theme.fallback.nameNoir": "Name",
  "theme.fallback.nameIndex": "John Doe",
  "theme.fallback.identity": "What do you do?",
  "theme.fallback.identityUpper": "YOUR ONE LINE IDENTITY AND MISSION",
  "theme.fallback.identityIndex": "Unknown",
  "theme.fallback.identityNoir": "Identity",
  "theme.fallback.terminalName": "guest",
  "theme.fallback.terminalIntro": "还没有填写一句话介绍",
  "theme.terminal.commandLabel": "终端命令",
  "theme.terminal.commandSubmit": "运行命令",
  "theme.terminal.outputLabel": "终端交互输出",
  "theme.connect": "CONNECT",
  "theme.markdown.tableRegion": "正文表格，可横向滚动",
  "theme.markdown.task": "任务",
  "theme.tags": "TAGS",
  "theme.verified": "VERIFIED",
  "theme.name": "NAME",
  "theme.identityLabel": "IDENTITY",
  "theme.keywords": "KEYWORDS",
  "theme.scroll": "Scroll",
  "theme.pixel.previous": "上一屏",
  "theme.pixel.next": "下一屏",
  "theme.pixel.screen": "第 {current} 屏，共 {total} 屏",
  "theme.arcade.stage": "关卡 {current} / {total}",
  "theme.noise.reseed": "重排噪声",
  "theme.readingTime": "约 {seconds} 秒读完",
  "theme.readingUnit": "秒读完",
  "theme.letter.greeting": "你好，",
  "theme.letter.signoff": "祝好，",
  "theme.letter.postscript": "可以在这些地方找到我",
  "theme.letter.written": "写于 {date}",
  "dashboard.loginTitle": "请先登录后管理你的介绍。",
  "dashboard.loginAction": "登录并管理",
  "dashboard.bannedTitle": "该账号暂不可使用。",
  "dashboard.bannedCopy": "如果你认为这是误判，请通过举报与申诉入口联系我们。",
  "connections.kicker": "账户",
  "connections.title": "登录方式",
  "connections.settingsKicker": "设置",
  "connections.settingsTitle": "账户与连接",
  "connections.settingsCopy": "管理登录身份和第三方 Agent 授权。",
  "connections.backToDashboard": "返回管理页",
  "connections.emailCode": "邮箱验证码",
  "connections.alwaysAvailable": "始终可用",
  "connections.connect": "连接",
  "connections.disconnect": "解除 30s.to 关联",
  "connections.notConnected": "未连接",
  "connections.noEmail": "未提供邮箱",
  "connections.emailVerified": "邮箱已验证",
  "connections.connected": "已连接",
  "connections.unavailable": "暂未启用",
  "connections.confirm": "确定解除 {provider} 与 30s.to 的关联吗？邮箱登录仍然保留。",
  "connections.removed": "已解除 30s.to 内的身份关联。",
  "connections.failed": "登录方式更新失败，请稍后重试。",
  "connections.help": "解除只删除 30s.to 的身份映射，不会撤销平台授权。管理平台授权：",
  "agentConnections.kicker": "Agent",
  "agentConnections.title": "Agent 连接",
  "agentConnections.copy": "安装一个 Skill，让你常用的 Agent 帮你收集信息、起草并安全更新 30s.to 草稿。",
  "agentConnections.addTitle": "连接新的 Agent",
  "agentConnections.addCopy": "查看连接地址和设置步骤",
  "agentConnections.examplesDisclosure": "查看使用示例",
  "agentConnections.endpoint": "连接地址",
  "agentConnections.copyAction": "复制",
  "agentConnections.copied": "已复制",
  "agentConnections.copyFailed": "复制失败，请手动复制。",
  "agentConnections.endpointCopied": "连接地址已复制。",
  "agentConnections.stepsAria": "连接 Agent 的步骤",
  "agentConnections.step1": "在 Agent 的 MCP 或连接器设置中添加远程服务。",
  "agentConnections.step2": "粘贴连接地址，然后登录 30s.to 并确认授权。",
  "agentConnections.step3": "告诉 Agent 要修改什么；完成后回到 30s.to 复核草稿。",
  "agentConnections.examplesTitle": "可以这样说",
  "agentConnections.exampleFirst": "读取我的 30s.to 资料和格式规则，帮我完善第一次介绍。",
  "agentConnections.exampleSection": "保持原有语气，只重写“现在”这一节。",
  "agentConnections.exampleShorten": "把全文压缩到 30 秒内，不要直接发布。",
  "agentConnections.copyExample": "复制示例 {number}",
  "agentConnections.promptCopied": "示例指令已复制。",
  "agentConnections.boundary": "Agent 只能读取和更新草稿，不能替你发布或停止公开。",
  "agentConnections.authorizedTitle": "已授权连接",
  "agentConnections.empty": "还没有授权任何 Agent 连接。",
  "agentConnections.disabled": "Agent 连接暂未启用。",
  "agentConnections.scopeRead": "读取草稿",
  "agentConnections.scopeWrite": "更新草稿",
  "agentConnections.connectedAt": "{date} 连接",
  "agentConnections.selfReported": "客户端名称由客户端自行提供",
  "agentConnections.revoke": "撤销",
  "agentConnections.confirm": "确定撤销 {client} 的连接吗？撤销后，它将无法继续访问你的 30s.to 草稿。",
  "agentConnections.confirmSkill": "确定撤销 30s.to Skill 的连接吗？撤销后，使用这个 Skill 的本地 Agent 将同时退出 30s.to。",
  "agentConnections.revoked": "Agent 连接已撤销。",
  "agentConnections.failed": "Agent 连接更新失败，请稍后重试。",
  "agentConnections.manualTitle": "手动连接 Remote MCP",
  "agentConnections.manualCopy": "高级用法：为支持 Remote MCP 的客户端单独授权",
  "skill.meta.title": "30s.to Skill — 让 Agent 写好你的 30 秒介绍",
  "skill.meta.description": "发来主页、项目或作品链接，让 Agent 主动了解你，通过采访写好你的 30 秒介绍。",
  "skill.kicker": "30s.to Skill",
  "skill.title": "自我介绍，\n交给你的 Agent。",
  "skill.copy": "给它几个链接，它会主动了解你，帮你写好一份 30 秒自我介绍。",
  "skill.processTitle": "它怎么帮你写",
  "skill.process.clues.title": "发来链接",
  "skill.process.clues.copy": "公开主页、项目网站或作品集。",
  "skill.process.research.title": "充分调研，再采访",
  "skill.process.research.copy": "深入研究你的主页、项目和作品，梳理出重点，再通过采访补全资料里没有的信息。",
  "skill.process.profile.title": "写好你的介绍",
  "skill.process.profile.copy": "一起调整内容和语气，写出你愿意分享的介绍。",
  "skill.installTitle": "安装到你的 Agent",
  "skill.installCopy": "复制指令，粘贴到你常用的 Agent 并发送。",
  "skill.promptLabel": "安装指令",
  "skill.copyPrompt": "复制安装指令",
  "skill.promptCopied": "安装指令已复制。下一步：打开你常用的 Agent，粘贴并发送刚复制的指令。",
  "skill.copyFailed": "复制失败，请手动复制下方的安装指令。",
  "skill.startTitle": "如何开始",
  "skill.start.install": "安装 30s.to Skill",
  "skill.start.chat": "开始聊",
  "skill.start.chatCopy": "安装完成后，新开一个对话，告诉它：",
  "skill.start.prompt": "用 30s.to 帮我写一份自我介绍。",
  "settings.title": "设置",
  "settings.copy": "管理账户、安全、外部连接和 30s.to 方案。",
  "settings.navAria": "设置分类",
  "settings.account.title": "账户",
  "settings.account.copy": "你的 30s.to 身份和个人介绍入口。",
  "settings.account.email": "账户邮箱",
  "settings.account.profile": "个人介绍",
  "settings.account.plan": "当前方案",
  "settings.account.manageProfile": "管理介绍",
  "settings.login.copy": "管理可用于登录 30s.to 的身份。",
  "settings.billing.copy": "查看当前方案、续费状态与账单入口。",
  "settings.invites.copy": "管理 Public Beta 邀请额度和最近的邀请码。",
  "dashboard.kicker": "我的介绍",
  "dashboard.defaultTitle": "我的 30 秒介绍",
  "dashboard.primaryEdit": "继续编辑",
  "dashboard.primaryCreate": "开始创建",
  "dashboard.unpublished": "尚未发布",
  "dashboard.unselected": "尚未选择",
  "dashboard.confirmDiscard": "确定放弃未发布修改，恢复到当前公开版本吗？",
  "dashboard.discardFailed": "放弃修改失败。",
  "dashboard.discardConflict": "草稿已在其他位置更新，请刷新页面确认后再试。",
  "dashboard.discardNetworkFailed": "放弃修改失败，请稍后重试。",
  "dashboard.discardDone": "已恢复到当前公开版本。",
  "dashboard.statusAria": "当前状态",
  "dashboard.versionAria": "版本关系",
  "dashboard.publicPage": "公开页",
  "dashboard.indexingAllowed": "允许搜索收录",
  "dashboard.indexingBlocked": "仅通过链接",
  "dashboard.publicVersion": "已发布版本",
  "dashboard.invisible": "别人暂时看不到",
  "dashboard.draft": "草稿",
  "dashboard.currentDraft": "当前编辑版本",
  "dashboard.draftSynced": "与公开页同步",
  "dashboard.editableVersionEmpty": "编辑版本 · 尚未创建",
  "dashboard.editableVersionDirty": "编辑版本 · 有修改未发布",
  "dashboard.editableVersionSynced": "编辑版本 · 与公开页一致",
  "dashboard.editableVersionUnpublished": "编辑版本 · 尚未发布",
  "dashboard.betaActive": "已激活",
  "dashboard.betaInactive": "未激活",
  "dashboard.adminCanPublish": "管理员账号可发布",
  "dashboard.betaAccessNote": "Public Beta 发布权限",
  "dashboard.visibility": "公开性",
  "dashboard.chooseOnPublish": "发布确认时选择",
  "dashboard.snapshot": "快照",
  "dashboard.snapshotAfterPublish": "发布后生成公开页快照",
  "dashboard.publishDetailsAria": "发布详情",
  "dashboard.actionsAria": "管理操作",
  "dashboard.nextAction": "下一步",
  "dashboard.openPublicPage": "打开公开页",
  "dashboard.changeVisibility": "修改公开方式",
  "dashboard.unpublish": "停止公开",
  "dashboard.unpublishTitle": "停止公开？",
  "dashboard.unpublishDescription": "公开页将立即停止访问；草稿和公开地址会保留，之后可以重新发布。",
  "dashboard.unpublishWarning": "搜索引擎或他人已经保存的副本可能不会立即消失。",
  "dashboard.unpublishConfirm": "停止公开",
  "dashboard.unpublishBusy": "正在停止...",
  "dashboard.unpublishSuccess": "公开页已停止访问，草稿和公开地址已保留。",
  "dashboard.unpublishFailed": "无法停止公开，请稍后重试。",
  "dashboard.discardBusy": "恢复中...",
  "dashboard.discard": "放弃修改",
  "dashboard.metricsAria": "轻量数据",
  "dashboard.views": "访问",
  "dashboard.clicks": "点击",
  "dashboard.readingTime": "阅读时间",
  "dashboard.theme": "主题",
  "dashboard.noTheme": "尚未选择",
  "dashboard.section.status": "状态",
  "dashboard.section.performance": "表现",
  "dashboard.fullAnalytics": "查看完整分析",
  "dashboard.shareCard": "分享卡片",
  "dashboard.timestamp.notPublished": "尚未发布",
  "dashboard.timestamp.notEdited": "尚未编辑",
  "dashboard.timestamp.published": "发布于 {date} UTC",
  "dashboard.timestamp.edited": "编辑于 {date} UTC",
  "upgrade.publishTheme": "发布 {theme}",
  "upgrade.copy": "你的选择已经保存，也可以继续完整预览。升级后无需重新编辑，回来即可发布。",
  "upgrade.benefit.themes": "发布全部高级主题",
  "upgrade.benefit.analytics": "查看阅读时间与完读分析",
  "upgrade.benefit.cards": "主题名片同步社交 OG",
  "upgrade.seePro": "查看 30s Pro",
  "upgrade.keepEditing": "继续编辑",
  "pro.meta.title": "30s Pro — 表达更像自己",
  "pro.meta.description": "高级主题、阅读质量分析与主题分享名片，让你的 30 秒介绍更像自己。",
  "pro.title": "表达更像自己。",
  "pro.intro": "内容仍然只有 30 秒。Pro 给你更多表达方式、更清楚的反馈，以及随手就能分享的素材。",
  "pro.priceAction": "查看 Founding Price",
  "pro.summaryAria": "Pro 权益摘要",
  "pro.summaryThemes": "款高级主题",
  "pro.summaryAnalytics": "天分析",
  "pro.summaryShare": "与名片同步",
  "pro.benefitsKicker": "Pro 工具箱",
  "pro.benefitsTitle": "从表达，到被看见。",
  "pro.benefit.themes.title": "高级主题",
  "pro.benefit.themes.body": "Terminal 领衔的 8 款主题。Free 可以完整预览，Pro 才能发布。",
  "pro.benefit.analytics.title": "阅读质量",
  "pro.benefit.analytics.body": "不只看访问量：查看有效阅读时间、完读率与进度分布，再结合 30 / 90 / 365 天趋势与链接 CTR。",
  "pro.benefit.share.title": "分享也像你",
  "pro.benefit.share.body": "Free 只有名字与链接的基础卡；Pro 提供两款通用名片与每个主题的专属名片。选定即同步为 OG，QR 融入卡面，可直接分享与下载。",
  "pro.benefit.attribution.title": "只留下你",
  "pro.benefit.attribution.body": "隐藏主题内部的 30s.to 署名；举报与语言等必要公共功能仍然保留。",
  "pro.pricingTitle": "越早加入，越简单。",
  "pro.pricingBody": "连续订阅期间保留 Founding Price。取消生效后再次加入，将使用届时价格。",
  "pro.terminalAria": "Terminal Pro 主题示意",
  "pro.checkout.continuous": "连续订阅，即可保留",
  "pro.checkout.intervalAria": "订阅周期",
  "pro.checkout.monthly": "月付",
  "pro.checkout.yearly": "年付",
  "pro.checkout.save": "省 $16",
  "pro.checkout.periodYear": "年",
  "pro.checkout.periodMonth": "月",
  "pro.checkout.annualNote": "相当于每月 ${price}，比月付节省 44%。",
  "pro.checkout.monthlyNote": "灵活按月订阅，随时在周期末取消。",
  "pro.checkout.includeThemes": "全部高级主题",
  "pro.checkout.includeAnalytics": "阅读质量与 365 天分析",
  "pro.checkout.includeCards": "主题名片、QR 与 OG",
  "pro.checkout.includeAttribution": "隐藏主题内署名",
  "pro.checkout.manage": "管理我的 Pro",
  "pro.checkout.comingSoon": "结账即将开放",
  "pro.checkout.signIn": "登录后升级",
  "pro.checkout.inviteRequired": "需要 Beta 邀请资格",
  "pro.checkout.opening": "正在打开安全结账…",
  "pro.checkout.upgrade": "升级到 30s Pro",
  "pro.checkout.footnote": "由 Creem 安全结账。适用税费将在付款前显示；无试用，自动续费。",
  "pro.checkout.unavailable": "暂时无法打开结账，请稍后重试。",
  "pro.checkout.networkError": "网络连接失败，请稍后重试。",
  "billing.panelAria": "Pro 与账单",
  "billing.plan": "方案",
  "billing.status.canceling": "已安排取消",
  "billing.status.pastDue": "付款逾期",
  "billing.status.active": "有效",
  "billing.status.free": "免费",
  "billing.yearly": "年付",
  "billing.monthly": "月付",
  "billing.basicPlan": "基础方案",
  "billing.ends": "结束 {date}",
  "billing.renews": "续费 {date}",
  "billing.graceEnds": "宽限期至：{date}",
  "billing.anomaly": "账单状态需要人工检查，请联系 support@30s.to。",
  "billing.opening": "打开中",
  "billing.manage": "账单",
  "billing.upgrade": "升级 Pro",
  "billing.openingHistory": "正在打开…",
  "billing.viewHistory": "查看历史账单",
  "billing.portalUnavailable": "暂时无法打开订阅管理。",
  "billing.networkError": "网络连接失败，请稍后重试。",
  "billingReturn.kicker": "30s Pro · 安全确认",
  "billingReturn.activeTitle": "Pro 已开通",
  "billingReturn.failedTitle": "还没有确认付款",
  "billingReturn.waitingTitle": "正在确认付款",
  "billingReturn.failedCopy": "付款状态可能仍在同步。你可以返回管理页稍后查看；我们不会仅凭回跳页面开通权益。",
  "billingReturn.waitingCopy": "正在和支付服务核对订阅状态，请不要关闭页面。",
  "billingReturn.back": "返回",
  "analytics.kicker": "30s Pro · 分析",
  "analytics.nav": "数据",
  "analytics.title": "从看见，到读完。",
  "analytics.copy": "从访问到完读与互动，而不是追踪个人。所有日期与去重口径均使用 UTC。",
  "analytics.rangeAria": "时间范围",
  "analytics.days": "{count} 天",
  "analytics.uniqueVisitors": "独立访客",
  "analytics.rangeTotal": "{count} 天累计",
  "analytics.averageReading": "平均有效阅读",
  "analytics.activeReadingNote": "可见且聚焦的阅读时间",
  "analytics.completionRate": "完读率",
  "analytics.completedVisitors": "{count} 位访客完成阅读",
  "analytics.overallCtr": "总体 CTR",
  "analytics.engagedVisitors": "{count} 位互动访客",
  "analytics.trafficTrend": "访问趋势",
  "analytics.dailyTrendAria": "每日独立访客趋势",
  "analytics.readingCompletion": "完读分布",
  "analytics.dailyMaximum": "访客 / 当日最高进度",
  "analytics.visitorCount": "{count} 人",
  "analytics.topLinks": "热门链接",
  "analytics.uniqueClickers": "独立点击者",
  "analytics.linkTrendAria": "{label} 每日趋势",
  "analytics.noInteractions": "还没有链接互动。",
  "analytics.audienceOverview": "访客概览",
  "analytics.pageLevel": "页面级",
  "analytics.referrers": "来源",
  "analytics.devices": "设备",
  "analytics.countries": "国家",
  "analytics.noData": "暂无数据",
  "analytics.durationSeconds": "{seconds} 秒",
  "analytics.durationMinutes": "{minutes} 分 {seconds} 秒",
  "analytics.gate.signInTitle": "登录后查看分析",
  "analytics.gate.signInCopy": "你的访问与互动数据会安全地留在管理页。",
  "analytics.gate.signInAction": "登录",
  "analytics.gate.proTitle": "看见你的介绍是否被读完。",
  "analytics.gate.proCopy": "有效阅读时间、完读率与进度分布，再结合 30、90、365 天趋势和链接 CTR，看清表达哪里真正抵达。",
  "analytics.gate.proAction": "查看 30s Pro",
  "analytics.gate.emptyTitle": "发布后，数据从这里开始。",
  "analytics.gate.emptyCopy": "先完成你的 30 秒介绍；发布后即可看到访问与互动趋势。",
  "analytics.gate.emptyAction": "开始创建",
  "analytics.gate.privacyNote": "UTC 日聚合 · 匿名去重 · 不保存原始 IP",
  "share.card": "分享卡片",
  "share.variant.classic": "经典",
  "share.variant.classicNote": "留白",
  "share.variant.contact": "索引",
  "share.variant.contactNote": "秩序",
  "share.variant.theme": "{theme} 专属",
  "share.variant.themeNote": "主题",
  "share.variant.universal": "通用",
  "share.saveFailed": "保存失败",
  "share.styleUpdated": "样式已更新",
  "share.networkError": "网络连接失败",
  "share.linkCopied": "链接已复制",
  "share.copyFailed": "复制失败",
  "share.text": "我的 30 秒介绍",
  "share.failed": "分享失败",
  "share.published": "已发布",
  "share.draft": "草稿",
  "share.style": "样式",
  "share.alt": "{name} 的分享卡",
  "share.publishFirst": "发布后可分享",
  "share.action": "分享",
  "share.download": "下载",
  "share.openPublicPage": "打开公开页",
  "share.chooseStyle": "选择卡片样式",
  "share.close": "关闭",
  "share.cardStyle": "卡片样式",
  "share.chooseOne": "选择一个样式",
  "share.chooseStyleAria": "选择分享卡样式",
  "share.saving": "保存中",
  "dashboard.visibility.indexed": "允许搜索引擎收录",
  "dashboard.visibility.unlisted": "仅通过链接",
  "dashboard.visibility.indexedNote": "可能出现在搜索结果中",
  "dashboard.visibility.unlistedNote": "拿到直接链接的人仍可查看",
  "dashboard.snapshot.ready": "缓存正常",
  "dashboard.snapshot.failed": "D1 fallback",
  "dashboard.snapshot.none": "未生成",
  "dashboard.snapshot.readyNote": "公开页快照已写入 KV",
  "dashboard.snapshot.failedNote": "公开页仍通过 D1 fallback 可访问",
  "dashboard.snapshot.noneNote": "当前已发布版本没有可用快照",
  "invite.title": "邀请朋友加入 Public Beta",
  "invite.remaining": "本月剩余 {remaining} / {quota}。",
  "invite.generateFailed": "生成失败。",
  "invite.generated": "{count} 个邀请码已生成，只会显示这一次。",
  "invite.quotaUsed": "本月邀请额度已用完。",
  "invite.generating": "生成中...",
  "invite.generate": "一键生成剩余 {count} 个",
  "invite.batchTitle": "本次生成的 {count} 个邀请码",
  "invite.copyAll": "复制全部邀请码",
  "invite.copiedAll": "已复制全部邀请码",
  "invite.copyMessage": "复制分享文案",
  "invite.messageCopied": "已复制分享文案",
  "invite.share": "直接分享",
  "invite.shared": "已打开分享面板。",
  "invite.shareFallback": "当前设备不支持直接分享，分享文案已复制。",
  "invite.shareFailed": "暂时无法分享，请复制分享文案。",
  "invite.shareMessage": "我在用 30s.to 做一份 30 秒个人介绍，也邀请你来试试。\n\n邀请码：\n{codes}\n\n打开 {url} 开始创建。",
  "invite.recentAria": "邀请码使用状态",
  "invite.statusTitle": "使用状态",
  "invite.statusSummary": "{active} 个可用 · {redeemed} 个已使用 · {expired} 个已过期 · {revoked} 个已撤销",
  "invite.status.revoked": "已撤销",
  "invite.status.redeemed": "已使用",
  "invite.status.expired": "已过期",
  "invite.status.active": "可用",
  "invite.copy": "复制",
  "invite.copied": "已复制",
  "invite.showOnce": "只显示这一次",
  "invite.expiryUnknown": "有效期未知",
  "invite.expiresAt": "有效期至 {date}",
  "management.handlePending.title": "地址变更待发布",
  "management.handlePending.description": "当前公开地址仍是 {publishedAddress}，发布后新地址 {draftAddress} 生效。",
  "management.handlePending.next": "发布修改后，新地址 {draftAddress} 才会成为公开地址。",
  "management.dirty.title": "有未发布修改",
  "management.dirty.description": "草稿还没有发布到公开页。",
  "management.dirty.next": "发布修改后，公开页会同步当前草稿。",
  "management.published.title": "已发布",
  "management.published.description": "公开页已是最新。",
  "management.published.next": "公开页已同步；需要调整内容时继续编辑即可。",
  "management.unpublished.title": "未发布",
  "management.unpublished.description": "这份介绍还只保存在草稿里，别人还看不到。",
  "management.unpublished.next": "发布后，别人才能通过公开地址访问这份介绍。",
  "management.empty.title": "还没有介绍",
  "management.empty.description": "先选择地址并写一段 30 秒介绍。",
  "management.empty.next": "先创建草稿；发布前检查会在编辑页处理。",
  "management.publish.create": "发布介绍",
  "management.publish.update": "发布修改",
  "abuse.kicker": "Trust & Safety",
  "abuse.title": "举报页面",
  "abuse.copy": "举报只用于安全审核，不会公开你的身份。",
  "abuse.missingHandle": "缺少举报对象。",
  "abuse.done": "已收到举报，我们会尽快处理。",
  "abuse.failed": "提交失败，请稍后重试。",
  "abuse.reason.spam": "垃圾内容",
  "abuse.reason.impersonation": "冒充他人",
  "abuse.reason.abuse": "骚扰或仇恨",
  "abuse.reason.illegal": "违法内容",
  "abuse.reason.other": "其他问题",
  "abuse.submitBusy": "提交中...",
  "abuse.submit": "提交举报",
  "terms.title": "服务条款",
  "privacy.title": "隐私说明",
  "validation.handle.required": "请输入 handle。",
  "validation.handle.invalid": "handle 只能使用 4-24 位小写英文字母。",
  "validation.handle.reserved": "这个公开地址不可用，换一个试试。",
  "validation.handle.unavailable": "这个公开地址不可用，换一个试试。",
  "validation.handle.frozen": "这个公开地址不可用，换一个试试。",
  "validation.handle.protected": "这个公开地址不可用，换一个试试。",
  "validation.handle.checkFailed": "暂时无法确认地址状态。",
  "validation.handle.changeLimit": "30 天内只能更改一次公开地址。",
  "validation.displayName.required": "姓名/昵称必填。",
  "validation.identity.required": "一句话身份必填。",
  "validation.tags.range": "标签需要填写 1-5 个。",
  "validation.links.limit": "独立链接最多 {limit} 个。",
  "validation.links.empty": "还没有添加链接；可以补充社交主页、预约或联系方式。",
  "validation.input.tooLong": "输入内容最多 {limit} 个字符。",
  "validation.body.placeholder": "正文仍包含{detail}，请替换成真实内容或删除。",
  "validation.body.placeholderDetail.legacyTemplate": "旧正文模板",
  "validation.body.placeholderDetail.ellipsis": "`...` 占位符",
  "validation.body.placeholderDetail.sampleLink": "示例链接 `https://example.com`",
  "validation.body.placeholderDetail.projectName": "「项目名」占位",
  "validation.body.placeholderDetail.instruction": "模板说明「一句话说明它解决什么问题」",
  "validation.body.oldTemplate": "旧正文模板",
  "validation.body.readingTime": "当前预计 {seconds} 秒，请精简到 {maximum} 秒内。",
  "validation.body.readingTimeLong": "当前预计 {seconds} 秒，超过建议的 {recommended} 秒，但仍可发布。",
  "validation.body.readingTimeShort": "当前预计 {seconds} 秒，内容偏少；建议补充到至少 {minimum} 秒。",
  "validation.markdown.h1": "不支持 # 页面级标题",
  "validation.markdown.deepHeading": "不支持 ### 或更深标题",
  "validation.markdown.image": "不支持图片语法",
  "validation.markdown.codeBlock": "不支持代码块",
  "validation.markdown.taskList": "不支持任务列表",
  "validation.markdown.table": "不支持表格",
  "validation.markdown.footnote": "不支持脚注",
  "validation.markdown.html": "不支持 HTML 或嵌入",
  "validation.markdown.commentUnclosed": "正文注释缺少结束标记 -->",
  "validation.markdown.commentStrayClose": "正文注释缺少开始标记 <!--",
  "validation.markdown.commentNested": "正文注释不支持嵌套 <!-- -->",
  "validation.markdown.nestedHeading": "章节标题只能位于文档根级",
  "validation.markdown.setextHeading": "不支持 Setext 标题",
  "validation.markdown.headingInline": "章节标题只能包含纯文本",
  "validation.markdown.frontmatter": "不支持 frontmatter",
  "validation.markdown.indentedCode": "不支持缩进代码块，请使用围栏代码块",
  "validation.markdown.codeFenceUnclosed": "代码围栏缺少结束标记",
  "validation.markdown.codeLanguage": "代码语言标签只允许 1–32 个字母、数字、_、+ 或 -",
  "validation.markdown.listDepth": "列表最多支持三级嵌套",
  "validation.markdown.tableSize": "表格最多支持 6 列、20 行",
  "validation.markdown.linkProtocol": "正文链接只支持 HTTPS 和邮箱",
  "validation.link.unsupportedType": "暂不支持此链接类型，请选择网站、平台或预约链接。",
  "validation.link.labelRequired": "链接名称不能为空。",
  "validation.link.providerRequired": "请选择预约平台。",
  "validation.link.valueRequired": "{label} 不能为空。",
  "validation.link.httpsOnly": "链接只支持 https://。",
  "validation.link.invalid": "请输入有效链接。",
  "validation.link.validEmail": "请输入有效邮箱。",
  "validation.link.validGithub": "请输入有效的 GitHub handle。",
  "validation.link.validBilibili": "请输入有效的 Bilibili UID。",
  "validation.link.validXiaohongshu": "请输入有效的小红书主页链接。",
  "validation.link.validGoogleCalendar": "请输入有效的 Google Calendar 预约链接。",
  "validation.link.validMicrosoftBookings": "请输入有效的 Microsoft Bookings 链接。",
  "validation.link.validHubSpot": "请输入有效的 HubSpot Meetings 链接。",
  "validation.account.banned": "该账号暂不可发布。",
  "validation.theme.proRequired": "这个主题需要 30s Pro，升级后即可发布。",
  "validation.account.login": "请先登录。",
  "validation.account.invite": "请先使用邀请码激活账号。",
  "validation.publish.failed": "发布失败，请稍后重试。",
  "validation.turnstile.required": "请完成人机校验。",
  "validation.report.missing": "缺少举报对象。",
  "validation.report.reason": "请选择举报原因。",
  "validation.report.notReportable": "这个页面当前不可举报。"
} as const;

export type MessageKey = keyof typeof zhCN_MESSAGES;
export const MESSAGE_KEYS = Object.keys(zhCN_MESSAGES) as MessageKey[];
export type Messages = Record<MessageKey, string>;

const en_MESSAGES: Record<MessageKey, string> = {
  "publish.handleReclaimed": "Reclaiming counts as an address change. Your current address will start a new 30-day redirect and 180-day protection period.",
  "handleEdit.reclaimAvailable": "You can reclaim this previous address.",
  "handleEdit.usePrevious": "Use this address",
  "handleEdit.deadlines": "Redirect ends {redirectDate}; protection ends {releaseDate}.",
  "handleEdit.previous": "Protected previous addresses",
  "global.siteDescription": "Introduce yourself thoughtfully in 30 seconds and help people understand you quickly.",
  "global.languageSwitch": "Switch language",
  "global.languageCurrent": "Current language: {locale}",
  "global.language.zh-CN": "中文",
  "global.language.zh-TW": "繁體中文",
  "global.language.en": "English",
  "global.language.ja": "日本語",
  "global.language.ko": "한국어",
  "global.language.es": "Español",
  "global.language.fr": "Français",
  "global.language.de": "Deutsch",
  "global.language.pt-BR": "Português (Brasil)",
  "global.language.ru": "Русский",
  "global.language.ar": "العربية",
  "common.close": "Close",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.manage": "Manage",
  "common.login": "Log in",
  "common.logout": "Log out",
  "common.loggingOut": "Logging out",
  "common.logoutRetry": "Retry log out",
  "common.preview": "Preview",
  "common.email": "Email",
  "common.inviteCode": "Invite code",
  "common.loading": "Loading",
  "editor.publish.processing": "Still processing",
  "common.localDraft": "Local draft",
  "common.publicBeta": "Public beta",
  "common.serviceStatus": "Service status",
  "common.optional": "Optional",
  "common.page": "Page",
  "common.reason": "Reason",
  "common.description": "Details",
  "common.submit": "Submit",
  "common.listSeparator": ", ",
  "nav.login": "Log in",
  "nav.draftFirst": "Start a draft",
  "home.action.create": "Create my intro",
  "home.action.continue": "Keep writing",
  "home.action.manage": "Manage my intro",
  "home.action.agent": "Agent writes it",
  "home.meta.title": "30s.to — 30 seconds. Let people get to know you.",
  "home.meta.description": "When you go solo, being understood matters even more. 30 seconds. Let people get to know you. Who you are, what you’re doing, what you’ve done, and what resources you can offer. Help people find what interests them and connect with you.",
  "home.title": "30 seconds.\nLet people get to know you.",
  "home.freeNote": "Create, publish, and share. Free forever.",
  "home.kicker": "When you go solo, being understood matters even more.",
  "home.value.copy": "Who you are, what you’re doing, what you’ve done, and what resources you can offer. Help people find what interests them and connect with you.",
  "home.features.label": "Product features",
  "home.features.time.tab": "Read in 30 seconds",
  "home.why.copy": "Write only what matters most. The longer the intro, the fewer people finish it. 30 seconds is just right.",
  "home.principle.expression.title": "Express yourself",
  "home.principle.expression.copy": "Organize your experience, work, ideas, and resources your way. Share what you want people to know about you.",
  "home.principle.link.title": "Your own 30s.to link",
  "home.principle.link.copy": "30s.to/@you is short, memorable, and to the point: get to know you in 30 seconds.",
  "home.principle.theme.title": "Themes that feel like you",
  "home.principle.theme.copy": "Clean, retro, or bold and vivid. Pick a theme that feels like you and let people get a sense of your style at first glance.",
  "home.footer.tagline": "Say less, so what matters gets seen.",
  "home.footer.navigation": "Footer navigation",
  "home.demo.linkNotice": "This is a demo link. Create your 30-second intro to add your own.",
  "home.demo.profile.name": "Maya Chen",
  "home.demo.profile.identity": "Independent product designer turning complex problems into simple products",
  "home.demo.profile.tag.product": "Product design",
  "home.demo.profile.tag.indie": "Indie products",
  "home.demo.profile.tag.ai": "AI tools",
  "home.demo.profile.body": "## Right now\nI’m building Briefly so independent designers spend less time moving notes and more time understanding people. Before designing, I walk through the real task with a user.\n\n## My background\n2020–2023: product designer on a collaboration software team, responsible for the editor and onboarding. I worked with engineers to turn complex operations into manageable steps.\n\nIndependent since 2024, doing research and interaction design for early teams while building my own small products.\n\n## My projects\n- [Relay](https://example.com/projects/relay): I designed this team decision log to keep conclusions, reasons and owners together, so newcomers can understand the context.\n- [Briefly](https://example.com/projects/briefly): in a small pilot, organizing interview notes into traceable themes while preserving original quotes for designers to check.\n\n## What I can offer\nHave feedback but unsure what to change first? I can help frame the problem and build a testable prototype. I also share interview guides and research-note templates with teams starting user research.\n\n## Let’s talk\nI’d like to meet people making thoughtful small products and discuss first interviews, tradeoffs and independent work. On weekends I explore neighborhood shops: a handwritten price list can teach me more than a product manual.",
  "home.demo.profile.link.website": "Work",
  "home.demo.profile.link.github": "GitHub",
  "home.demo.profile.link.calendar": "Book a chat",
  "handleStart.kicker": "Public address",
  "handleStart.title": "Choose your 30s.to address",
  "handleStart.copy": "Pick a memorable address first, then write your 30-second intro. Any existing draft content stays with you.",
  "handleStart.label": "Your address",
  "handleStart.help.empty": "4-24 lowercase letters. This only saves a draft; availability is confirmed on publish.",
  "handleStart.help.ok": "Format looks good. Availability is checked again on publish.",
  "handleStart.submit": "Continue writing",
  "handleAvailability.checking": "Checking this address…",
  "handleAvailability.available": "Available now. It becomes yours after a successful publish.",
  "handleAvailability.owned": "You already own this address.",
  "handleAvailability.assigned": "This address is assigned to you and takes effect after publishing.",
  "handleAvailability.failed": "We could not confirm this address. Please check again.",
  "handleAvailability.rateLimited": "Too many checks. Try again in {minutes} minutes.",
  "handleAvailability.retry": "Check again",
  "handleAvailability.suggestions": "Or try",
  "handleAvailability.cooldown": "Your address is in the 30-day change cooldown until {date}. You can still restore your currently published address.",
  "editor.shell.loading": "Loading create flow",
  "editor.shell.workspace": "30-second intro workspace",
  "editor.shell.canvas": "30-second intro writing surface",
  "editor.command.aria": "Editor command bar",
  "editor.command.publish": "Publish",
  "editor.command.publishUpdate": "Publish changes",
  "editor.command.publishSettings": "Publish settings",
  "editor.command.dashboard": "Dashboard",
  "editor.publish.success": "Published",
  "editor.publish.successAfterLogin": "Logged in and published",
  "editor.publish.publishing": "Publishing…",
  "editor.publish.publishingDetail": "We’re preparing your public page. This should only take a moment.",
  "editor.publish.successDetail": "Your public page is up to date and ready to view.",
  "editor.publish.viewPublic": "View public page",
  "editor.publish.cacheFailed": "but writing the public-page cache failed.",
  "editor.command.login": "Log in",
  "editor.handle.editTitle": "Edit public address",
  "editor.handle.claimAfterPublish": "Yours after publish",
  "editor.handle.claimAfterPublishTitle": "This address becomes yours after publishing",
  "editor.status.signedIn": "Signed in",
  "editor.status.account": "Account: {email}",
  "editor.status.localTitle": "Local draft",
  "editor.status.localCopy": "This draft is only saved on this device. Sign in to sync it to the cloud.",
  "editor.status.location": "Save location",
  "editor.status.state": "Save status",
  "editor.status.emailLogin": "Email login",
  "editor.autosave.savingShort": "Saving",
  "editor.autosave.savedCloudShort": "Saved to cloud",
  "editor.autosave.savedLocalShort": "Saved locally",
  "editor.autosave.failedShort": "Save failed",
  "editor.autosave.signedInShort": "Signed in",
  "editor.autosave.cloud": "Cloud",
  "editor.autosave.retry": "Needs retry",
  "editor.autosave.device": "This device",
  "editor.autosave.saving": "Auto-saving...",
  "editor.autosave.savedLocal": "Auto-saved locally",
  "editor.autosave.savedCloud": "Auto-saved to cloud",
  "editor.autosave.authExpired": "Session expired. Saved locally.",
  "editor.autosave.cloudFailed": "Cloud save failed. Saved locally.",
  "editor.autosave.cloudUnavailable": "Cloud saving is not connected yet. Saved locally.",
  "editor.autosave.failed": "Auto-save failed. Copy your content and retry.",
  "editor.autosave.enabled": "Auto-save is on",
  "identity.namePlaceholder": "Your name",
  "identity.nameAria": "Name or nickname",
  "identity.identityPlaceholder": "One-line identity",
  "identity.identityAria": "One-line identity",
  "identity.tagsAria": "Tags",
  "identity.addTag": "Add tag",
  "identity.removeTag": "Remove tag {tag}",
  "body.emptyInstruction": "Write the few things that matter most when someone first meets you.\nWrite less so what matters gets seen. 30s is just right.",
  "body.templatesLabel": "Or start with a template",
  "body.templatesAria": "Body identity templates",
  "body.templateExampleLabel": "Sample profile",
  "body.viewTemplateExample": "View example",
  "links.sectionAria": "Links",
  "links.documentTitle": "Links",
  "links.documentCopy": "Social profiles, booking, and contact details",
  "links.documentEmptyCopy": "Add a social profile, booking page, or contact method. You can change them anytime.",
  "links.add": "Add link",
  "links.addType": "Add {label}",
  "links.edit": "Edit {label}",
  "links.addedAria": "Added links",
  "links.closePanel": "Close link editor",
  "links.editTitle": "Edit link",
  "links.addTitle": "Add link",
  "links.chooseTypeAgain": "Choose link type again",
  "links.calendarProvider": "Booking provider",
  "links.displayName": "Display name",
  "links.duplicate": "{label} has already been added.",
  "links.alreadyAdded": "{label} already added",
  "links.bookingAlreadyAdded": "Booking has already been added",
  "links.remove": "Delete",
  "links.category.social": "Social",
  "links.category.booking": "Booking",
  "links.category.work": "Work / websites",
  "links.category.content": "Content",
  "links.category.contact": "Contact",
  "links.category.other": "Other",
  "links.type.x": "X",
  "links.type.github": "GitHub",
  "links.type.linkedin": "LinkedIn",
  "links.type.instagram": "Instagram",
  "links.type.youtube": "YouTube",
  "links.type.bilibili": "Bilibili",
  "links.type.xiaohongshu": "Xiaohongshu",
  "links.type.wechat": "WeChat Official Account",
  "links.type.email": "Email",
  "links.type.calendar": "Booking",
  "links.type.website": "Website",
  "links.type.newsletter": "Newsletter",
  "links.type.tiktok": "TikTok",
  "links.type.threads": "Threads",
  "links.type.mastodon": "Mastodon",
  "links.type.dribbble": "Dribbble",
  "links.type.jike": "Jike",
  "links.type.other": "Other",
  "links.input.x": "X handle",
  "links.input.github": "GitHub handle",
  "links.input.linkedin": "LinkedIn URL",
  "links.input.instagram": "Instagram handle",
  "links.input.youtube": "YouTube handle",
  "links.input.bilibili": "Bilibili UID",
  "links.input.xiaohongshu": "Xiaohongshu profile URL",
  "links.input.wechat": "WeChat Official Account ID",
  "links.input.email": "Email",
  "links.input.calendar": "Booking path",
  "links.input.website": "Website URL",
  "links.input.newsletter": "Newsletter URL",
  "links.input.tiktok": "TikTok handle",
  "links.input.threads": "Threads handle",
  "links.input.mastodon": "Mastodon URL",
  "links.input.dribbble": "Dribbble handle",
  "links.input.jike": "Jike profile URL",
  "links.input.other": "Link URL",
  "links.input.calendar.cal": "Cal.com path",
  "links.input.calendar.calendly": "Calendly path",
  "links.input.calendar.google": "Google Calendar booking page",
  "links.input.calendar.microsoft": "Microsoft Bookings link",
  "links.input.calendar.hubspot": "HubSpot Meetings link",
  "links.input.calendar.savvycal": "SavvyCal path",
  "links.input.calendar.tidycal": "TidyCal path",
  "links.input.calendar.zcal": "zcal path",
  "links.hint.handle": "Enter only the handle, not a URL.",
  "links.hint.uid": "Enter only the UID, not the profile URL.",
  "links.hint.slug": "Enter only the path, not the full URL.",
  "links.hint.email": "This creates a mailto link.",
  "links.hint.text": "Clicking on the public page copies this value.",
  "links.hint.url": "Enter a valid https:// link for this platform.",
  "links.hint.calendarSlug": "Enter only the username or booking path, not the full URL.",
  "links.hint.calendarUrl": "Enter the full booking-page URL for this platform.",
  "rich.toolbarAria": "Body formatting toolbar",
  "rich.heading": "Section heading",
  "rich.paragraph": "Paragraph",
  "rich.bold": "Bold",
  "rich.boldFallback": "bold text",
  "rich.italic": "Italic",
  "rich.italicFallback": "italic text",
  "rich.code": "Inline code",
  "rich.comment": "Toggle comment",
  "rich.ul": "Bulleted list",
  "rich.ol": "Numbered list",
  "rich.link": "Link",
  "rich.linkPrompt": "Enter an https:// or mailto: link",
  "rich.linkInvalid": "Links only support https:// or mailto:",
  "rich.linkFallback": "link text",
  "rich.aria": "30-second intro body",
  "rich.more": "More formatting",
  "rich.quote": "Quote",
  "rich.strikethrough": "Strikethrough",
  "rich.strikethroughFallback": "struck text",
  "rich.codeBlock": "Code block",
  "rich.codeBlockFallback": "code",
  "rich.taskList": "Task list",
  "rich.table": "Table",
  "rich.tableSkeleton": "| Item | Description |\n| --- | --- |\n| Item one | Description one |\n| Item two | Description two |",
  "rich.indent": "Increase indent",
  "rich.outdent": "Decrease indent",
  "rich.placeholder": "Write a Markdown intro that can be read in 30 seconds...",
  "feedback.aria": "Writing feedback",
  "feedback.timeTitle": "Estimated reading time",
  "feedback.preview": "Preview",
  "feedback.previewMenu": "Open preview menu",
  "feedback.openAcceptancePreview": "Open acceptance preview",
  "feedback.acceptancePreviewCopy": "View the public-page result on this device",
  "feedback.noIssues": "No blocking issues",
  "feedback.prePublish": "Pre-publish checks",
  "feedback.ready": "No blockers",
  "feedback.itemCount": "{count} items",
  "feedback.valid": "Content passes publish checks.",
  "issue.action.displayName": "Add name",
  "issue.action.oneLineIdentity": "Add identity",
  "issue.action.tags": "Add tags",
  "issue.action.body": "Back to body",
  "issue.action.handle": "Choose address",
  "issue.action.links": "Manage links",
  "issue.action.view": "View",
  "issue.short.displayName": "Missing name",
  "issue.short.oneLineIdentity": "Missing identity",
  "issue.short.tags": "Missing tags",
  "issue.short.handle": "Missing address",
  "issue.short.readingTime": "Over 60s",
  "issue.short.readingTimeLong": "Over 30s",
  "issue.short.readingTimeShort": "Too little content",
  "issue.short.bodyPlaceholder": "Body placeholder",
  "issue.short.bodyFormat": "Body format",
  "issue.short.links": "Link issue",
  "issue.short.linksEmpty": "Links can be added",
  "issue.short.pending": "Needs work",
  "issue.summary.more": "{count} items to fix",
  "publish.title.create": "Confirm publish",
  "publish.title.update": "Confirm changes",
  "publish.action.create": "Publish to 30s.to",
  "publish.action.update": "Publish changes",
  "publish.closeAria": "Close publish confirmation",
  "publish.address": "Public address",
  "publish.unpublishedChanges": "Unpublished changes",
  "publish.handleChanged": "Your public address will change from @{oldHandle} to @{newHandle}. The old address redirects for 30 days. After 180 days from the change, it may be assigned to someone else and old links may show their profile.",
  "publish.readingTime": "Reading time",
  "publish.status": "Status",
  "publish.ready": "Ready",
  "publish.todo": "{count} items to improve",
  "publish.visibility": "Publishing visibility",
  "publish.visibility.indexed": "Allow search indexing",
  "publish.visibility.unlisted": "Link only",
  "publish.visibility.indexedNote": "Anyone can view it, and it may appear in search results.",
  "publish.visibility.unlistedNote": "Anyone with the link can view it; we will not submit it to search engines.",
  "publish.visibility.publicNote": "Both options are public, and the link can be forwarded. Keep it as a draft if only you should see it.",
  "publish.publicContent": "Publishing makes your name, identity, tags, body, and links public. Author comments stay hidden, but should not contain secrets.",
  "publish.noop": "The public page is already up to date. Change content or visibility before publishing again.",
  "publish.valid": "Content passes publish checks.",
  "handleEdit.title": "Edit public address",
  "handleEdit.label": "30s.to address",
  "handleEdit.help.changed": "Saving changes the draft only. After publishing, the old address redirects for 30 days and may be reassigned after 180 days.",
  "handleEdit.help.saved": "Saved to draft. Availability is checked again on publish.",
  "handleEdit.note": "The current public address is still 30s.to/@{handle}. The new address takes effect after publishing.",
  "handleEdit.save": "Save to draft",
  "conflict.title": "Choose draft version",
  "conflict.copy": "This device and the cloud both have unpublished content. Your choice becomes the next version to edit.",
  "conflict.useLocal": "Use local draft",
  "conflict.useLocalCopy": "Keep the current page content and sync it to the cloud.",
  "conflict.useCloud": "Use cloud draft",
  "conflict.untitled": "Untitled",
  "conflict.cloudVersion": "Version saved in the cloud",
  "conflict.modifiedAt": "Last edited · {time}",
  "conflict.time.justNow": "just now",
  "conflict.time.minutesAgo": "{count} min ago",
  "conflict.time.hoursAgo": "{count} hr ago",
  "conflict.time.daysAgo": "{count} days ago",
  "authModal.title": "Log in to 30s.to",
  "authModal.copy": "After login, drafts sync to the cloud and publishing is more reliable.",
  "login.page.eyebrow": "Account",
  "login.page.title": "Log in to 30s.to",
  "login.page.copy": "Sync drafts, publish public pages, and manage invites.",
  "login.invalidEmail": "Enter a valid email.",
  "login.sendFailed": "Could not send the code.",
  "login.devCodeReady": "Local development code generated.",
  "login.codeSent": "Code sent. Check your email.",
  "login.networkSendFailed": "Could not send the code. Check your connection.",
  "login.invalidCode": "Enter the 6-digit code.",
  "login.codeInvalid": "The code is wrong or expired.",
  "login.failed": "Login failed. Try again later.",
  "login.sendBusy": "Sending...",
  "login.resendCode": "Resend code",
  "login.sendCode": "Send code",
  "login.localCode": "Local development code: {code}",
  "login.code": "Code",
  "login.codePlaceholder": "6 digits",
  "login.inviteHelp": "Public Beta requires an invite code. Verification activates your account.",
  "login.verifyBusy": "Verifying...",
  "login.verifyInvite": "Verify invite and log in",
  "login.verify": "Log in and continue",
  "login.footnote": "Passwordless login. New users activate with an invite after email verification.",
  "login.google": "Continue with Google",
  "login.github": "Continue with GitHub",
  "login.emailDivider": "or use email",
  "login.oauthFailed": "Social login did not finish. Try again or use email login.",
  "login.linkTitle": "Verify account email",
  "login.linkPageCopy": "A 30s.to account already uses this email. We link it only after verification; accounts are never auto-merged.",
  "login.linkCopy": "We will send a code to {email}. The email cannot be changed here.",
  "login.linkVerify": "Verify and link",
  "login.linkRequired": "An existing account was found. Verify its email to continue.",
  "login.activateTitle": "Activate 30s.to",
  "login.activatePageCopy": "Your social identity is verified. No permanent account is created until the invite succeeds.",
  "login.activateCopy": "Public Beta requires an invite. It is redeemed only when account creation succeeds.",
  "login.activationOpenCopy": "Registration is open. Continue to activate your account.",
  "login.activationClosedCopy": "New account activation is paused. Existing accounts can still sign in.",
  "login.activate": "Activate and continue",
  "login.inviteRequired": "Email verified. Use an invite to activate.",
  "login.useEmailInstead": "Use email login instead",
  "preview.emptyEyebrow": "Preview",
  "preview.emptyTitle": "No intro available to preview.",
  "preview.backToEdit": "Back to edit",
  "preview.edit": "Edit",
  "preview.localNoteAria": "Local preview note",
  "preview.localNote": "Local acceptance",
  "preview.localTitle": "Draft preview on this device",
  "preview.themePicker": "Theme switcher",
  "preview.themeGroup": "Choose theme",
  "preview.themeFree": "Base",
  "preview.themePro": "Pro",
  "preview.mobileAria": "Mobile public-page preview",
  "preview.close": "Close preview",
  "preview.editorTitle": "Preview",
  "preview.publicTitle": "Public page preview",
  "preview.shareCardButton": "View theme card",
  "preview.shareCardDialogTitle": "Theme card preview",
  "preview.shareCardTitle": "{theme} theme card",
  "preview.shareCardLogin": "Sign in to render the real preview",
  "preview.shareCardLoading": "Rendering the theme card…",
  "preview.shareCardError": "The card preview is temporarily unavailable",
  "preview.shareCardNote": "The theme card uses the same visual language as the public page. Universal cards are available in Dashboard.",
  "public.copySuccess": "Copied {label}",
  "public.copyFailed": "Copy failed. Please copy manually.",
  "public.createMine": "Create my intro",
  "public.report": "Report this page",
  "public.ownerAria": "Personal management entry",
  "public.owner.manage": "Manage",
  "public.owner.edit": "Edit",
  "public.status.unavailable": "Unavailable",
  "public.status.notFound": "404",
  "public.status.moderated": "This 30-second intro is not available.",
  "public.status.frozen": "This public address is not available.",
  "public.status.unpublished": "This 30-second intro has not been published yet.",
  "theme.footer": "Made with 30s.to",
  "theme.fallback.name": "Your Name",
  "theme.fallback.nameUpper": "YOUR NAME",
  "theme.fallback.nameNoir": "Name",
  "theme.fallback.nameIndex": "John Doe",
  "theme.fallback.identity": "What do you do?",
  "theme.fallback.identityUpper": "YOUR ONE LINE IDENTITY AND MISSION",
  "theme.fallback.identityIndex": "Unknown",
  "theme.fallback.identityNoir": "Identity",
  "theme.fallback.terminalName": "guest",
  "theme.fallback.terminalIntro": "No short intro yet",
  "theme.terminal.commandLabel": "Terminal command",
  "theme.terminal.commandSubmit": "Run command",
  "theme.terminal.outputLabel": "Interactive terminal output",
  "theme.connect": "CONNECT",
  "theme.markdown.tableRegion": "Content table, horizontally scrollable",
  "theme.markdown.task": "Task",
  "theme.tags": "TAGS",
  "theme.verified": "VERIFIED",
  "theme.name": "NAME",
  "theme.identityLabel": "IDENTITY",
  "theme.keywords": "KEYWORDS",
  "theme.scroll": "Scroll",
  "theme.pixel.previous": "Previous screen",
  "theme.pixel.next": "Next screen",
  "theme.pixel.screen": "Screen {current} of {total}",
  "theme.arcade.stage": "Stage {current} / {total}",
  "theme.noise.reseed": "Reseed noise",
  "theme.readingTime": "{seconds}s read",
  "theme.readingUnit": "sec read",
  "theme.letter.greeting": "Hello,",
  "theme.letter.signoff": "Warm regards,",
  "theme.letter.postscript": "You can find me here",
  "theme.letter.written": "Written {date}",
  "dashboard.loginTitle": "Log in to manage your intro.",
  "dashboard.loginAction": "Log in and manage",
  "dashboard.bannedTitle": "This account is currently unavailable.",
  "dashboard.bannedCopy": "If you believe this is a mistake, contact us through the report and appeal entry.",
  "connections.kicker": "Account",
  "connections.title": "Login methods",
  "connections.settingsKicker": "Settings",
  "connections.settingsTitle": "Account & connections",
  "connections.settingsCopy": "Manage login identities and third-party agent access.",
  "connections.backToDashboard": "Back to dashboard",
  "connections.emailCode": "Email verification code",
  "connections.alwaysAvailable": "Always available",
  "connections.connect": "Connect",
  "connections.disconnect": "Remove 30s.to link",
  "connections.notConnected": "Not connected",
  "connections.noEmail": "No email provided",
  "connections.emailVerified": "Email verified",
  "connections.connected": "Connected",
  "connections.unavailable": "Not enabled",
  "connections.confirm": "Remove the {provider} link from 30s.to? Email login remains available.",
  "connections.removed": "The identity link was removed from 30s.to.",
  "connections.failed": "Could not update login methods. Try again later.",
  "connections.help": "This only removes the 30s.to identity mapping; it does not revoke provider access. Manage provider access:",
  "agentConnections.kicker": "Agent",
  "agentConnections.title": "Agent connections",
  "agentConnections.copy": "Install one Skill so your preferred agents can gather evidence, draft, and safely update your 30s.to draft.",
  "agentConnections.addTitle": "Connect a new agent",
  "agentConnections.addCopy": "View the connection URL and setup steps",
  "agentConnections.examplesDisclosure": "View usage examples",
  "agentConnections.endpoint": "Connection URL",
  "agentConnections.copyAction": "Copy",
  "agentConnections.copied": "Copied",
  "agentConnections.copyFailed": "Could not copy. Copy the URL manually.",
  "agentConnections.endpointCopied": "Connection URL copied.",
  "agentConnections.stepsAria": "How to connect an agent",
  "agentConnections.step1": "Add a remote service in your agent's MCP or connector settings.",
  "agentConnections.step2": "Paste the connection URL, then sign in to 30s.to and approve access.",
  "agentConnections.step3": "Tell the agent what to change, then return to 30s.to to review the draft.",
  "agentConnections.examplesTitle": "Try asking",
  "agentConnections.exampleFirst": "Read my 30s.to profile and formatting rules, then help me complete my first intro.",
  "agentConnections.exampleSection": "Keep my existing tone and rewrite only the 'Now' section.",
  "agentConnections.exampleShorten": "Shorten the full intro to under 30 seconds. Do not publish it.",
  "agentConnections.copyExample": "Copy example {number}",
  "agentConnections.promptCopied": "Example prompt copied.",
  "agentConnections.boundary": "Agents can only read and update drafts. They cannot publish or stop publishing for you.",
  "agentConnections.authorizedTitle": "Authorized connections",
  "agentConnections.empty": "No agents have been authorized yet.",
  "agentConnections.disabled": "Agent connections are not enabled yet.",
  "agentConnections.scopeRead": "Read draft",
  "agentConnections.scopeWrite": "Update draft",
  "agentConnections.connectedAt": "Connected {date}",
  "agentConnections.selfReported": "Client name is self-reported by the client",
  "agentConnections.revoke": "Revoke",
  "agentConnections.confirm": "Revoke {client}? It will no longer be able to access your 30s.to draft.",
  "agentConnections.confirmSkill": "Revoke the 30s.to Skill connection? Local agents using this Skill will be signed out of 30s.to.",
  "agentConnections.revoked": "Agent connection revoked.",
  "agentConnections.failed": "Could not update agent connections. Try again later.",
  "agentConnections.manualTitle": "Connect Remote MCP manually",
  "agentConnections.manualCopy": "Advanced: authorize a Remote MCP-compatible client separately",
  "skill.meta.title": "30s.to Skill — Let your agent write your 30-second intro",
  "skill.meta.description": "Share your site, projects, or portfolio. Agent researches your work and interviews you to write your 30-second intro.",
  "skill.kicker": "30s.to Skill",
  "skill.title": "Your introduction,\nwritten with your agent.",
  "skill.copy": "Give it a few links. It gets to know you and helps write your 30-second intro.",
  "skill.processTitle": "How Agent helps you write",
  "skill.process.clues.title": "Share links",
  "skill.process.clues.copy": "Your public profile, project website, or portfolio.",
  "skill.process.research.title": "Thorough research, then an interview",
  "skill.process.research.copy": "It researches your website, projects, and work in depth, draws out the key points, then interviews you to fill the gaps.",
  "skill.process.profile.title": "Agent writes your intro",
  "skill.process.profile.copy": "Refine the content and tone together until it feels like you.",
  "skill.installTitle": "Install it in your agent",
  "skill.installCopy": "Copy the prompt, paste it into your usual agent, and send it.",
  "skill.promptLabel": "Installation prompt",
  "skill.copyPrompt": "Copy installation prompt",
  "skill.promptCopied": "Installation prompt copied. Next, open the agent you use, paste the prompt, and send it.",
  "skill.copyFailed": "Could not copy. Select and copy the installation prompt below.",
  "skill.startTitle": "How to start",
  "skill.start.install": "Install the 30s.to Skill",
  "skill.start.chat": "Start a conversation",
  "skill.start.chatCopy": "Once installed, open a new conversation and say:",
  "skill.start.prompt": "Use 30s.to to help write my introduction.",
  "settings.title": "Settings",
  "settings.copy": "Manage your account, security, external connections, and 30s.to plan.",
  "settings.navAria": "Settings categories",
  "settings.account.title": "Account",
  "settings.account.copy": "Your 30s.to identity and profile entry point.",
  "settings.account.email": "Account email",
  "settings.account.profile": "Profile",
  "settings.account.plan": "Current plan",
  "settings.account.manageProfile": "Manage profile",
  "settings.login.copy": "Manage the identities you can use to sign in to 30s.to.",
  "settings.billing.copy": "Review your current plan, renewal status, and billing access.",
  "settings.invites.copy": "Manage your Public Beta invite allowance and recent invite codes.",
  "dashboard.kicker": "My intro",
  "dashboard.defaultTitle": "My 30-second intro",
  "dashboard.primaryEdit": "Continue editing",
  "dashboard.primaryCreate": "Start creating",
  "dashboard.unpublished": "Not published",
  "dashboard.unselected": "Not selected",
  "dashboard.confirmDiscard": "Discard unpublished changes and restore the current public version?",
  "dashboard.discardFailed": "Could not discard changes.",
  "dashboard.discardConflict": "This draft changed elsewhere. Refresh and review it before trying again.",
  "dashboard.discardNetworkFailed": "Could not discard changes. Try again later.",
  "dashboard.discardDone": "Restored to the current public version.",
  "dashboard.statusAria": "Current status",
  "dashboard.versionAria": "Version relationship",
  "dashboard.publicPage": "Public page",
  "dashboard.indexingAllowed": "Search indexing allowed",
  "dashboard.indexingBlocked": "Link only",
  "dashboard.publicVersion": "Published version",
  "dashboard.invisible": "Not visible to others yet",
  "dashboard.draft": "Draft",
  "dashboard.currentDraft": "Current editable version",
  "dashboard.draftSynced": "Synced with the public page",
  "dashboard.editableVersionEmpty": "Editable version · Not created",
  "dashboard.editableVersionDirty": "Editable version · Changes not published",
  "dashboard.editableVersionSynced": "Editable version · Matches public page",
  "dashboard.editableVersionUnpublished": "Editable version · Not published",
  "dashboard.betaActive": "Active",
  "dashboard.betaInactive": "Inactive",
  "dashboard.adminCanPublish": "Admin account can publish",
  "dashboard.betaAccessNote": "Public Beta publishing access",
  "dashboard.visibility": "Visibility",
  "dashboard.chooseOnPublish": "Choose during publish confirmation",
  "dashboard.snapshot": "Snapshot",
  "dashboard.snapshotAfterPublish": "Public page snapshot is generated after publishing",
  "dashboard.publishDetailsAria": "Publishing details",
  "dashboard.actionsAria": "Management actions",
  "dashboard.nextAction": "Next step",
  "dashboard.openPublicPage": "Open public page",
  "dashboard.changeVisibility": "Change publishing visibility",
  "dashboard.unpublish": "Stop publishing",
  "dashboard.unpublishTitle": "Stop publishing?",
  "dashboard.unpublishDescription": "The public page will become unavailable immediately. Your draft and public address will be kept so you can publish again.",
  "dashboard.unpublishWarning": "Copies already saved by search engines or other people may not disappear immediately.",
  "dashboard.unpublishConfirm": "Stop publishing",
  "dashboard.unpublishBusy": "Stopping...",
  "dashboard.unpublishSuccess": "The public page is no longer available. Your draft and public address were kept.",
  "dashboard.unpublishFailed": "Could not stop publishing. Try again later.",
  "dashboard.discardBusy": "Restoring...",
  "dashboard.discard": "Discard changes",
  "dashboard.metricsAria": "Lightweight data",
  "dashboard.views": "Views",
  "dashboard.clicks": "Clicks",
  "dashboard.readingTime": "Reading time",
  "dashboard.theme": "Theme",
  "dashboard.noTheme": "Not selected",
  "dashboard.section.status": "Status",
  "dashboard.section.performance": "Performance",
  "dashboard.fullAnalytics": "View full analytics",
  "dashboard.shareCard": "Share card",
  "dashboard.timestamp.notPublished": "Not published",
  "dashboard.timestamp.notEdited": "Not edited",
  "dashboard.timestamp.published": "Published {date} UTC",
  "dashboard.timestamp.edited": "Edited {date} UTC",
  "upgrade.publishTheme": "Publish {theme}",
  "upgrade.copy": "Your selection is saved and remains fully previewable. Upgrade and return to publish without editing again.",
  "upgrade.benefit.themes": "Publish every premium theme",
  "upgrade.benefit.analytics": "See reading time and completion",
  "upgrade.benefit.cards": "Theme cards synced to social OG",
  "upgrade.seePro": "See 30s Pro",
  "upgrade.keepEditing": "Keep editing",
  "pro.meta.title": "30s Pro — Express yourself",
  "pro.meta.description": "Premium themes, reading-quality analytics, and share cards that make your 30-second intro feel more like you.",
  "pro.title": "Express yourself, unmistakably.",
  "pro.intro": "Your page still takes only 30 seconds to read. Pro gives you more ways to express it, clearer feedback, and assets ready to share.",
  "pro.priceAction": "See Founding Price",
  "pro.summaryAria": "Pro benefits summary",
  "pro.summaryThemes": "premium themes",
  "pro.summaryAnalytics": "days of analytics",
  "pro.summaryShare": "synced to your card",
  "pro.benefitsKicker": "Pro toolkit",
  "pro.benefitsTitle": "From expression to discovery.",
  "pro.benefit.themes.title": "Premium themes",
  "pro.benefit.themes.body": "Eight themes led by Terminal. Preview every theme on Free; publish them with Pro.",
  "pro.benefit.analytics.title": "Reading quality",
  "pro.benefit.analytics.body": "Go beyond visits with active reading time, completion rate, progress distribution, 30, 90, and 365-day trends, and link CTR.",
  "pro.benefit.share.title": "Share like yourself",
  "pro.benefit.share.body": "Free has one simple name-and-link card. Pro adds two universal cards and one design for every theme. Your selection becomes the OG image, with QR built in for sharing and download.",
  "pro.benefit.attribution.title": "Only you",
  "pro.benefit.attribution.body": "Hide in-theme 30s.to attribution while essential report and language controls remain available.",
  "pro.pricingTitle": "Join early. Keep it simple.",
  "pro.pricingBody": "Keep Founding Price while your subscription remains continuous. Rejoining after termination uses the then-current price.",
  "pro.terminalAria": "Terminal Pro theme preview",
  "pro.checkout.continuous": "Locked in while subscribed",
  "pro.checkout.intervalAria": "Billing interval",
  "pro.checkout.monthly": "Monthly",
  "pro.checkout.yearly": "Yearly",
  "pro.checkout.save": "Save $16",
  "pro.checkout.periodYear": "year",
  "pro.checkout.periodMonth": "month",
  "pro.checkout.annualNote": "${price} per month, 44% less than monthly.",
  "pro.checkout.monthlyNote": "Flexible monthly billing. Cancel at period end.",
  "pro.checkout.includeThemes": "All premium themes",
  "pro.checkout.includeAnalytics": "Reading quality and 365-day analytics",
  "pro.checkout.includeCards": "Theme cards, QR, and OG",
  "pro.checkout.includeAttribution": "Hide in-theme attribution",
  "pro.checkout.manage": "Manage my Pro",
  "pro.checkout.comingSoon": "Checkout coming soon",
  "pro.checkout.signIn": "Sign in to upgrade",
  "pro.checkout.inviteRequired": "Beta invite required",
  "pro.checkout.opening": "Opening secure checkout…",
  "pro.checkout.upgrade": "Upgrade to 30s Pro",
  "pro.checkout.footnote": "Secure checkout by Creem. Taxes are shown before payment; no trial, renews automatically.",
  "pro.checkout.unavailable": "Checkout is temporarily unavailable. Please try again.",
  "pro.checkout.networkError": "Network connection failed. Please try again.",
  "billing.panelAria": "Pro and billing",
  "billing.plan": "Plan",
  "billing.status.canceling": "Canceling",
  "billing.status.pastDue": "Past due",
  "billing.status.active": "Active",
  "billing.status.free": "Free",
  "billing.yearly": "Yearly",
  "billing.monthly": "Monthly",
  "billing.basicPlan": "Basic plan",
  "billing.ends": "Ends {date}",
  "billing.renews": "Renews {date}",
  "billing.graceEnds": "Grace period ends: {date}",
  "billing.anomaly": "Your billing status needs manual review. Contact support@30s.to.",
  "billing.opening": "Opening",
  "billing.manage": "Billing",
  "billing.upgrade": "Upgrade to Pro",
  "billing.openingHistory": "Opening…",
  "billing.viewHistory": "View billing history",
  "billing.portalUnavailable": "Billing management is temporarily unavailable.",
  "billing.networkError": "Network connection failed. Please try again.",
  "billingReturn.kicker": "30s Pro · Secure confirmation",
  "billingReturn.activeTitle": "Pro is active",
  "billingReturn.failedTitle": "Payment is not confirmed yet",
  "billingReturn.waitingTitle": "Confirming your payment",
  "billingReturn.failedCopy": "Your payment may still be syncing. Check Dashboard again later; access is never granted from the return page alone.",
  "billingReturn.waitingCopy": "We are checking your subscription with the payment provider. Keep this page open.",
  "billingReturn.back": "Go back",
  "analytics.kicker": "30s Pro · Analytics",
  "analytics.nav": "Analytics",
  "analytics.title": "From visits to completion.",
  "analytics.copy": "Follow visits through completion and interaction without tracking people. Dates and deduplication use UTC.",
  "analytics.rangeAria": "Date range",
  "analytics.days": "{count} days",
  "analytics.uniqueVisitors": "Unique visitors",
  "analytics.rangeTotal": "{count}-day total",
  "analytics.averageReading": "Avg. active reading",
  "analytics.activeReadingNote": "Visible and focused time",
  "analytics.completionRate": "Completion rate",
  "analytics.completedVisitors": "{count} visitors completed",
  "analytics.overallCtr": "Overall CTR",
  "analytics.engagedVisitors": "{count} engaged visitors",
  "analytics.trafficTrend": "Traffic trend",
  "analytics.dailyTrendAria": "Daily unique visitor trend",
  "analytics.readingCompletion": "Reading completion",
  "analytics.dailyMaximum": "Visitors / daily max",
  "analytics.visitorCount": "{count} visitors",
  "analytics.topLinks": "Top Links",
  "analytics.uniqueClickers": "Unique clickers",
  "analytics.linkTrendAria": "Daily trend for {label}",
  "analytics.noInteractions": "No link interactions yet.",
  "analytics.audienceOverview": "Audience overview",
  "analytics.pageLevel": "Page level",
  "analytics.referrers": "Referrers",
  "analytics.devices": "Devices",
  "analytics.countries": "Countries",
  "analytics.noData": "No data yet",
  "analytics.durationSeconds": "{seconds}s",
  "analytics.durationMinutes": "{minutes}m {seconds}s",
  "analytics.gate.signInTitle": "Sign in to see analytics",
  "analytics.gate.signInCopy": "Your traffic and interaction data stays safely inside your dashboard.",
  "analytics.gate.signInAction": "Sign in",
  "analytics.gate.proTitle": "See whether your intro gets read.",
  "analytics.gate.proCopy": "See active reading time, completion rate, progress distribution, 30, 90, and 365-day trends, and link CTR.",
  "analytics.gate.proAction": "Explore 30s Pro",
  "analytics.gate.emptyTitle": "Your data starts after publishing.",
  "analytics.gate.emptyCopy": "Finish your 30-second intro first. Traffic and interaction trends appear after publishing.",
  "analytics.gate.emptyAction": "Start creating",
  "analytics.gate.privacyNote": "UTC daily aggregation · Anonymous deduplication · No raw IP storage",
  "share.card": "Share card",
  "share.variant.classic": "Classic",
  "share.variant.classicNote": "Quiet",
  "share.variant.contact": "Index",
  "share.variant.contactNote": "Structured",
  "share.variant.theme": "{theme} edition",
  "share.variant.themeNote": "Theme",
  "share.variant.universal": "Universal",
  "share.saveFailed": "Could not save",
  "share.styleUpdated": "Style updated",
  "share.networkError": "Network connection failed",
  "share.linkCopied": "Link copied",
  "share.copyFailed": "Copy failed",
  "share.text": "My 30-second introduction",
  "share.failed": "Could not share",
  "share.published": "Published",
  "share.draft": "Draft",
  "share.style": "Style",
  "share.alt": "{name}'s share card",
  "share.publishFirst": "Publish to share",
  "share.action": "Share",
  "share.download": "Download",
  "share.openPublicPage": "Open public page",
  "share.chooseStyle": "Choose card style",
  "share.close": "Close",
  "share.cardStyle": "Card style",
  "share.chooseOne": "Choose a style",
  "share.chooseStyleAria": "Choose share-card style",
  "share.saving": "Saving",
  "dashboard.visibility.indexed": "Allow search indexing",
  "dashboard.visibility.unlisted": "Link only",
  "dashboard.visibility.indexedNote": "May appear in search results",
  "dashboard.visibility.unlistedNote": "Still visible to anyone with the direct link",
  "dashboard.snapshot.ready": "Cache ready",
  "dashboard.snapshot.failed": "D1 fallback",
  "dashboard.snapshot.none": "Not generated",
  "dashboard.snapshot.readyNote": "Public snapshot written to KV",
  "dashboard.snapshot.failedNote": "Public page still works through D1 fallback",
  "dashboard.snapshot.noneNote": "No snapshot is available for the current published version",
  "invite.title": "Invite friends to Public Beta",
  "invite.remaining": "{remaining} / {quota} invites left this month.",
  "invite.generateFailed": "Could not generate invite.",
  "invite.generated": "{count} invite codes generated. They are shown only once.",
  "invite.quotaUsed": "Monthly invite quota used.",
  "invite.generating": "Generating...",
  "invite.generate": "Generate all {count} remaining",
  "invite.batchTitle": "{count} invite codes generated now",
  "invite.copyAll": "Copy all codes",
  "invite.copiedAll": "All codes copied",
  "invite.copyMessage": "Copy share message",
  "invite.messageCopied": "Share message copied",
  "invite.share": "Share",
  "invite.shared": "Share sheet opened.",
  "invite.shareFallback": "Direct sharing is unavailable, so the share message was copied.",
  "invite.shareFailed": "Could not share. Copy the share message instead.",
  "invite.shareMessage": "I’m using 30s.to to make a 30-second personal introduction, and I’d love you to try it too.\n\nInvite codes:\n{codes}\n\nOpen {url} to get started.",
  "invite.recentAria": "Invite code usage status",
  "invite.statusTitle": "Usage status",
  "invite.statusSummary": "{active} available · {redeemed} used · {expired} expired · {revoked} revoked",
  "invite.status.revoked": "Revoked",
  "invite.status.redeemed": "Used",
  "invite.status.expired": "Expired",
  "invite.status.active": "Available",
  "invite.copy": "Copy",
  "invite.copied": "Copied",
  "invite.showOnce": "Shown only once",
  "invite.expiryUnknown": "Expiry unknown",
  "invite.expiresAt": "Expires on {date}",
  "management.handlePending.title": "Address change pending",
  "management.handlePending.description": "Current public address is still {publishedAddress}. Publishing activates {draftAddress}.",
  "management.handlePending.next": "Publish changes before {draftAddress} becomes the public address.",
  "management.dirty.title": "Unpublished changes",
  "management.dirty.description": "Your draft has not been published to the public page.",
  "management.dirty.next": "Publish changes to sync the public page with this draft.",
  "management.published.title": "Published",
  "management.published.description": "The public page is up to date.",
  "management.published.next": "The public page is synced. Continue editing when you want to adjust it.",
  "management.unpublished.title": "Unpublished",
  "management.unpublished.description": "This intro is still only saved as a draft.",
  "management.unpublished.next": "Publish it before others can visit the public address.",
  "management.empty.title": "No intro yet",
  "management.empty.description": "Choose an address and write a 30-second intro.",
  "management.empty.next": "Create a draft first. Pre-publish checks stay in the editor.",
  "management.publish.create": "Publish intro",
  "management.publish.update": "Publish changes",
  "abuse.kicker": "Trust & Safety",
  "abuse.title": "Report page",
  "abuse.copy": "Reports are used only for safety review. Your identity is not public.",
  "abuse.missingHandle": "Missing report target.",
  "abuse.done": "Report received. We will review it soon.",
  "abuse.failed": "Submission failed. Try again later.",
  "abuse.reason.spam": "Spam",
  "abuse.reason.impersonation": "Impersonation",
  "abuse.reason.abuse": "Harassment or hate",
  "abuse.reason.illegal": "Illegal content",
  "abuse.reason.other": "Other issue",
  "abuse.submitBusy": "Submitting...",
  "abuse.submit": "Submit report",
  "terms.title": "Terms of Service",
  "privacy.title": "Privacy Notice",
  "validation.handle.required": "Enter a handle.",
  "validation.handle.invalid": "Handle must be 4-24 lowercase English letters.",
  "validation.handle.reserved": "This public address is unavailable. Try another one.",
  "validation.handle.unavailable": "This public address is unavailable. Try another one.",
  "validation.handle.frozen": "This public address is unavailable. Try another one.",
  "validation.handle.protected": "This public address is unavailable. Try another one.",
  "validation.handle.checkFailed": "We could not confirm this address.",
  "validation.handle.changeLimit": "You can change your public address only once every 30 days.",
  "validation.displayName.required": "Name or nickname is required.",
  "validation.identity.required": "One-line identity is required.",
  "validation.tags.range": "Add 1-5 tags.",
  "validation.links.limit": "You can add up to {limit} standalone links.",
  "validation.links.empty": "No links yet. You can add a social profile, booking page, or contact method.",
  "validation.input.tooLong": "Input can contain at most {limit} characters.",
  "validation.body.placeholder": "Body still contains {detail}. Replace it with real content or delete it.",
  "validation.body.placeholderDetail.legacyTemplate": "the old body template",
  "validation.body.placeholderDetail.ellipsis": "the `...` placeholder",
  "validation.body.placeholderDetail.sampleLink": "the sample link `https://example.com`",
  "validation.body.placeholderDetail.projectName": "the `项目名` placeholder",
  "validation.body.placeholderDetail.instruction": "template instruction text",
  "validation.body.oldTemplate": "the old body template",
  "validation.body.readingTime": "Estimated reading time is {seconds} seconds. Keep it within {maximum} seconds.",
  "validation.body.readingTimeLong": "Estimated reading time is {seconds} seconds, above the recommended {recommended} seconds, but you can still publish.",
  "validation.body.readingTimeShort": "Estimated reading time is {seconds} seconds. Add enough detail to reach at least {minimum} seconds.",
  "validation.markdown.h1": "# page-level headings are not supported",
  "validation.markdown.deepHeading": "### and deeper headings are not supported",
  "validation.markdown.image": "Image syntax is not supported",
  "validation.markdown.codeBlock": "Code blocks are not supported",
  "validation.markdown.taskList": "Task lists are not supported",
  "validation.markdown.table": "Tables are not supported",
  "validation.markdown.footnote": "Footnotes are not supported",
  "validation.markdown.html": "HTML and embeds are not supported",
  "validation.markdown.commentUnclosed": "Body comment is missing its closing --> marker",
  "validation.markdown.commentStrayClose": "Body comment is missing its opening <!-- marker",
  "validation.markdown.commentNested": "Nested <!-- --> body comments are not supported",
  "validation.markdown.nestedHeading": "Section headings must be at the document root",
  "validation.markdown.setextHeading": "Setext headings are not supported",
  "validation.markdown.headingInline": "Section headings can only contain plain text",
  "validation.markdown.frontmatter": "Frontmatter is not supported",
  "validation.markdown.indentedCode": "Indented code blocks are not supported; use a fenced code block",
  "validation.markdown.codeFenceUnclosed": "Code fence is missing its closing marker",
  "validation.markdown.codeLanguage": "Code language tags may contain 1–32 letters, numbers, _, +, or -",
  "validation.markdown.listDepth": "Lists support up to three nesting levels",
  "validation.markdown.tableSize": "Tables support up to 6 columns and 20 rows",
  "validation.markdown.linkProtocol": "Body links only support HTTPS and email",
  "validation.link.unsupportedType": "This link type is not supported yet. Choose a website, platform, or booking link.",
  "validation.link.labelRequired": "Link name cannot be empty.",
  "validation.link.providerRequired": "Choose a booking platform.",
  "validation.link.valueRequired": "{label} cannot be empty.",
  "validation.link.httpsOnly": "Links only support https://.",
  "validation.link.invalid": "Enter a valid link.",
  "validation.link.validEmail": "Enter a valid email.",
  "validation.link.validGithub": "Enter a valid GitHub handle.",
  "validation.link.validBilibili": "Enter a valid Bilibili UID.",
  "validation.link.validXiaohongshu": "Enter a valid Xiaohongshu profile link.",
  "validation.link.validGoogleCalendar": "Enter a valid Google Calendar booking link.",
  "validation.link.validMicrosoftBookings": "Enter a valid Microsoft Bookings link.",
  "validation.theme.proRequired": "Publishing this theme requires 30s Pro.",
  "validation.link.validHubSpot": "Enter a valid HubSpot Meetings link.",
  "validation.account.banned": "This account cannot publish right now.",
  "validation.account.login": "Please log in first.",
  "validation.account.invite": "Activate your account with an invite code first.",
  "validation.publish.failed": "Publishing failed. Try again later.",
  "validation.turnstile.required": "Complete the human check.",
  "validation.report.missing": "Missing report target.",
  "validation.report.reason": "Choose a report reason.",
  "validation.report.notReportable": "This page cannot be reported right now."
};

const MESSAGES: Record<Locale, Record<MessageKey, string>> = {
  "zh-CN": zhCN_MESSAGES,
  "zh-TW": zhTWMessages,
  en: en_MESSAGES,
  ja: jaMessages,
  ko: koMessages,
  es: esMessages,
  fr: frMessages,
  de: deMessages,
  "pt-BR": ptBRMessages,
  ru: ruMessages,
  ar: arMessages
};

export function resolveLocaleFromHeaders(headers: Headers | { get(name: string): string | null }): Locale {
  const cookieLocale = readLocaleCookie(headers.get("cookie"));
  if (cookieLocale) return cookieLocale;
  return resolveLocaleFromAcceptLanguage(headers.get("accept-language")) ?? DEFAULT_LOCALE;
}

export function resolveLocaleFromAcceptLanguage(header?: string | null): Locale | null {
  if (!header) return null;
  const candidates = header
    .split(",")
    .map((item) => {
      const [rawLocale, ...parameters] = item.trim().split(";");
      const qualityParameter = parameters.map((parameter) => parameter.trim()).find((parameter) => /^q\s*=/i.test(parameter));
      const rawQuality = qualityParameter?.replace(/^q\s*=\s*/i, "");
      const quality = rawQuality === undefined ? 1 : Number(rawQuality);
      return { locale: normalizeLocale(rawLocale), quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((item): item is { locale: Locale; quality: number } => Boolean(item.locale) && item.quality > 0)
    .sort((left, right) => right.quality - left.quality);
  return candidates[0]?.locale ?? null;
}

export function readLocaleCookie(cookieHeader?: string | null): Locale | null {
  if (!cookieHeader) return null;
  const cookies = cookieHeader.split(";").map((item) => item.trim());
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.split("=");
    if (name !== LOCALE_COOKIE) continue;
    try {
      return normalizeLocale(decodeURIComponent(valueParts.join("=")));
    } catch {
      return null;
    }
  }
  return null;
}

export function t(locale: Locale, key: MessageKey, params: MessageParams = {}): string {
  const template = MESSAGES[locale][key] ?? MESSAGES[DEFAULT_LOCALE][key] ?? key;
  return template.replace(/\{([a-zA-Z0-9_.-]+)\}/g, (match, paramKey) => {
    const value = params[paramKey];
    return value === undefined || value === null ? match : String(value);
  });
}

export function getMessages(locale: Locale): Messages {
  return MESSAGES[locale];
}
